import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, FileText, Shield, Scale, Settings, GraduationCap, Users, X, Eye, Download, Calendar, ArrowRight } from 'lucide-react';
import s3Service from '../services/s3Service.js';
import { ConfigHelpers } from '../config/partnerConfig.js';
import { getDebugState, clearDashboardData, debugLog } from '../utils/debugUtils.js';
import PartnerOnboardingAPI from '../services/partnerOnboardingAPI.js';
import NDAModal from '../components/NDAModal.jsx';
import PartnershipAgreementModal from '../components/PartnershipAgreementModal.jsx';
import SetupMeetingModal from '../components/SetupMeetingModal.jsx';
import TrainingResourcesModal from '../components/TrainingResourcesModal.jsx';
import BusinessDocumentationModal from '../components/BusinessDocumentationModal.jsx';
import ShariyahCertificationModal from '../components/ShariyahCertificationModal.jsx';
import { useBusinessDocumentation } from '../hooks/useBusinessDocumentation.js';
import { RESOURCE_TYPES, RESOURCE_CONFIG, getAllResourceTypes, getResourceConfig } from '../config/resourceConfig.js';
import { formatFileSize } from '../utils/fileUtils.js';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [partnerData, setPartnerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState(null);
  const [showNDATerms, setShowNDATerms] = useState(false);
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [showSetupMeeting, setShowSetupMeeting] = useState(false);
  const [showCompletedDocument, setShowCompletedDocument] = useState(false);
  const [showPartnershipTerms, setShowPartnershipTerms] = useState(false);
  const [partnershipAgreed, setPartnershipAgreed] = useState(false);
  const [showPartnershipDocument, setShowPartnershipDocument] = useState(false);
  const [showShariyahCertification, setShowShariyahCertification] = useState(false);
  const [shariyahCompleted, setShariyahCompleted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [showLegalCompliance, setShowLegalCompliance] = useState(false);
  const [legalCompleted, setLegalCompleted] = useState(false);
  const [legalUploadedFiles, setLegalUploadedFiles] = useState([]);
  const [isLegalUploading, setIsLegalUploading] = useState(false);
  const [isViewingFile, setIsViewingFile] = useState(false);
  const [showTechSetup, setShowTechSetup] = useState(false);
  const [techSetupCompleted, setTechSetupCompleted] = useState(false);
  const [techSetupProgress, setTechSetupProgress] = useState({
    loanApplication: false,
    creditScoring: false,
    paymentSystem: false,
    auditTrail: false
  });
  const [showTrainingResources, setShowTrainingResources] = useState(false);
  const [trainingCompleted, setTrainingCompleted] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState({
    islamicFinance101: false,
    salesTraining: false,
    systemTraining: false,
    resourceAccess: false
  });
  const [trainingUploadedFiles, setTrainingUploadedFiles] = useState([]);
  const [isTrainingUploading, setIsTrainingUploading] = useState(false);
  const [trainingUploadProgress, setTrainingUploadProgress] = useState('');
  const [trainingUploadStatus, setTrainingUploadStatus] = useState('');
  const [isViewingTrainingFile, setIsViewingTrainingFile] = useState(false);
  const [showLaunchEngagement, setShowLaunchEngagement] = useState(false);
  const [launchCompleted, setLaunchCompleted] = useState(false);
  const [launchProgress, setLaunchProgress] = useState({
    pilotProgram: false,
    pricingStrategy: false,
    transparencyTools: false,
    communityOutreach: false
  });
  // Business Documentation - all state managed by hook
  const [partnerType, setPartnerType] = useState(''); // 'existing' or 'new'
  const [onboardingData, setOnboardingData] = useState(null);
  const [apiError, setApiError] = useState(null);
  // Resource files state
  const [resourceFiles, setResourceFiles] = useState({
    [RESOURCE_TYPES.ISLAMIC_FINANCE]: [],
    [RESOURCE_TYPES.SALES_TRAINING]: [],
    [RESOURCE_TYPES.SYSTEM_TRAINING]: [],
    [RESOURCE_TYPES.SHARIA_TEMPLATES]: [],
    [RESOURCE_TYPES.OTHER_RESOURCES]: [],
  });
  const [loadingResourceFiles, setLoadingResourceFiles] = useState(false);
  const isLoadingResourcesRef = useRef(false);

  // Initialize API
  const api = new PartnerOnboardingAPI();

  // Get current lifecycle steps based on configuration
  const getLifecycleSteps = () => {
    // Helper function to determine step status - now uses step keys
    const getStepStatus = (stepKey, isCompleted, prerequisitesCompleted) => {
      if (isCompleted) return 'completed';
      
      // Get prerequisites for this step (now returns step keys)
      const prerequisites = ConfigHelpers.getStepPrerequisites(stepKey);
      const enabledPrerequisites = prerequisites.filter(prereqKey => ConfigHelpers.isStepEnabled(prereqKey));
      
      // If no prerequisites or all prerequisites are disabled, step is available to start
      if (enabledPrerequisites.length === 0) {
        return 'in_progress';
      }
      
      // If prerequisites are not completed, step is pending
      if (!prerequisitesCompleted) return 'pending';
      
      return 'in_progress';
    };

    // Helper function to get step colors
    const getStepColors = (status) => {
      switch (status) {
        case 'completed':
          return {
            color: 'bg-green-500',
            borderColor: 'border-green-500',
            textColor: 'text-green-600'
          };
        case 'in_progress':
          return {
            color: 'bg-yellow-500',
            borderColor: 'border-yellow-500',
            textColor: 'text-yellow-600'
          };
        default: // pending
          return {
            color: 'bg-gray-300',
            borderColor: 'border-gray-300',
            textColor: 'text-gray-500'
          };
      }
    };

    // Get step completion states from API data - now uses step keys
    const getStepCompletionState = (stepKey) => {
      if (!onboardingData?.stepData) return false;
      // Step key matches the property name in stepData
      return onboardingData.stepData[stepKey]?.completed || false;
    };

    // Get step prerequisites completion - now uses step keys
    const getPrerequisitesCompleted = (stepKey) => {
      const prerequisites = ConfigHelpers.getStepPrerequisites(stepKey);
      // Prerequisites are now step keys, not numeric IDs
      // Only check prerequisites that are enabled in the configuration
      const enabledPrerequisites = prerequisites.filter(prereqKey => ConfigHelpers.isStepEnabled(prereqKey));
      
      // If there are no enabled prerequisites, consider prerequisites as completed
      if (enabledPrerequisites.length === 0) {
        return true;
      }
      
      return enabledPrerequisites.every(prereqKey => getStepCompletionState(prereqKey));
    };

    // Build steps array based on configuration
    const enabledSteps = ConfigHelpers.getEnabledSteps();
    
    return enabledSteps.map((stepConfig, index) => {
      // Get numeric ID from step key for backward compatibility
      const stepId = ConfigHelpers.getStepId(stepConfig.key) || (index + 1);
      const stepKey = stepConfig.key;
      const isCompleted = getStepCompletionState(stepKey);
      const prerequisitesCompleted = getPrerequisitesCompleted(stepKey);
      const status = getStepStatus(stepKey, isCompleted, prerequisitesCompleted);
      
      // Get step-specific data
      const stepData = getStepSpecificData(stepKey, isCompleted);
      
      return {
        id: stepId, // Numeric ID for backward compatibility
        key: stepKey, // Step key for new linked list structure
        title: stepConfig.title,
        description: stepConfig.description,
        icon: getStepIcon(stepKey),
        status: status,
        ...getStepColors(status),
        enabled: stepConfig.enabled,
        required: stepConfig.required,
        completedDate: isCompleted ? onboardingData.stepData[stepKey]?.completedAt : null,
        approvedBy: isCompleted ? stepData.approvedBy : null,
        details: stepData.details,
        config: stepConfig // Include configuration for reference
      };
    });
  };

  // Helper function to get step key - now accepts both numeric ID or key
  const getStepKey = (stepIdOrKey) => {
    // If it's already a string (key), return it
    if (typeof stepIdOrKey === 'string') {
      return stepIdOrKey;
    }
    // Otherwise, get step by ID and return its key
    const step = ConfigHelpers.getStepById(stepIdOrKey);
    return step ? step.key : '';
  };

  // Helper function to get step-specific data - now uses step keys
  const getStepSpecificData = (stepKey, isCompleted) => {
    switch (stepKey) {
      case 'nda':
        return {
          approvedBy: 'Partner Agreement',
          details: {
            summary: isCompleted 
              ? "Non-Disclosure Agreement has been signed and approved" 
              : "Please review and agree to the Non-Disclosure Agreement to continue",
            terms: [
              "Confidentiality of all business information",
              "Non-disclosure of proprietary technology", 
              "Protection of customer data and privacy",
              "Restrictions on information sharing",
              "Penalties for breach of agreement"
            ]
          }
        };
      case 'meeting':
        return {
          approvedBy: 'Partnership Team',
          details: {
            summary: isCompleted 
              ? "Meeting request has been submitted successfully. We'll contact you soon to schedule." 
              : "Schedule a meeting to discuss our partnership with you",
            steps: [
              "Fill out the meeting request form",
              "Select your preferred date and time",
              "We'll contact you to confirm the meeting",
              "Discuss partnership details and next steps"
            ]
          }
        };
      case 'partnership':
        return {
          approvedBy: 'Partner Agreement',
          details: {
            summary: isCompleted 
              ? "Partnership agreement has been finalized and executed" 
              : "Please review and agree to the Partnership Agreement to continue",
            terms: [
              "Mutual cooperation in financial services",
              "Revenue sharing model based on fair and just principles",
              "Joint marketing and business development",
              "Technology integration and data sharing",
              "Dispute resolution through professional arbitration"
            ]
          }
        };
      case 'productDevelopment':
        return {
          approvedBy: 'Product Development Team',
          details: {
            summary: isCompleted 
              ? "Product development completed successfully. Access to comprehensive resources available." 
              : "Complete product development by downloading templates, customizing them per your company policy, and uploading your customized documents",
            productDevelopmentSteps: [
              "Download product development templates and resources",
              "Review and customize templates per your company policy",
              "Get required approvals from your legal team",
              "Upload customized product development documents"
            ]
          }
        };
      case 'shariyah':
        return {
          approvedBy: 'Shariyah Advisor',
          details: {
            summary: isCompleted 
              ? "Shariyah certification has been completed successfully" 
              : "Please upload required documents for Shariyah certification review",
            requirements: [
              "Review of financial products for ethical and fair practices",
              "Assessment of business practices against Islamic principles",
              "Documentation of governance framework",
              "Approval from qualified Shariyah scholars",
              "Implementation of monitoring systems"
            ]
          }
        };
      case 'legal':
        return {
          approvedBy: 'Legal Review Team',
          details: {
            summary: isCompleted 
              ? "Legal compliance verification has been completed successfully" 
              : "Please upload required legal documents for regulatory verification",
            requirements: [
              "Regulatory license applications",
              "Compliance with financial services regulations",
              "Anti-money laundering (AML) procedures",
              "Know Your Customer (KYC) protocols",
              "Regular compliance reporting systems"
            ]
          }
        };
      case 'businessDocumentation':
        return {
          approvedBy: 'Business Review Team',
          details: {
            summary: isCompleted 
              ? "Business documentation has been submitted successfully" 
              : "Please upload confidential business documents for review",
            requirements: [
              "Risk management documentation",
              "Credit process summary",
              "Financial flow documentation",
              "Operational model overview"
            ]
          }
        };
      case 'techSetup':
        return {
          approvedBy: 'Tech Implementation Team',
          details: {
            summary: isCompleted 
              ? "Halal finance technology infrastructure has been successfully implemented" 
              : "Please complete the technology setup for halal loan processing",
            components: [
              "Halal loan application system",
              "Riba-free credit scoring",
              "Transparent payment processing",
              "Islamic audit trail system",
              "Customer portal for halal services"
            ]
          }
        };
      case 'launch':
        return {
          approvedBy: 'Launch Team',
          details: {
            summary: isCompleted 
              ? "Launch completed successfully. Community engagement and halal services are now active." 
              : "Complete launch components to engage with community and start halal services",
            launchComponents: [
              "Pilot Program: Community partnerships and workshops",
              "Pricing Strategy: Competitive riba-free pricing",
              "Transparency Tools: Client portal and fee transparency",
              "Community Outreach: Local engagement and education"
            ]
          }
        };
      default:
        return {
          approvedBy: 'System',
          details: {
            summary: "Step completed",
            requirements: []
          }
        };
    }
  };

  // Helper function to get step icon - now uses step keys
  const getStepIcon = (stepKey) => {
    switch (stepKey) {
      case 'nda': return FileText;
      case 'meeting': return Calendar;
      case 'partnership': return FileText;
      case 'productDevelopment': return GraduationCap;
      case 'shariyah': return Shield;
      case 'legal': return Scale;
      case 'businessDocumentation': return FileText;
      case 'techSetup': return Settings;
      case 'launch': return Users;
      default: return FileText;
    }
  };

  // Helper function to get partner name from login response (partnerInfo) or fallback to onboarding data
  const getPartnerName = () => {
    // First try to get from login response (stored in localStorage)
    try {
      const storedPartnerInfo = localStorage.getItem('partnerInfo');
      if (storedPartnerInfo) {
        const partnerInfo = JSON.parse(storedPartnerInfo);
        if (partnerInfo.partnerName) {
          return partnerInfo.partnerName;
        }
      }
    } catch (error) {
      alert('Error parsing partnerInfo from localStorage:', error);
    }
    return "Unknown Partner";
  };

  // Load onboarding data from API
  const loadOnboardingData = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      
      // Get user ID from localStorage or session
      const userId = localStorage.getItem('userId') || 'demo-user-id'; // Replace with actual user ID logic
      console.log('Loading onboarding data for userId:', userId);
      
      const response = await api.getOnboardingStatus(userId);
      
      if (response.status === 'success') {
        console.log('API response received:', response.data.onboarding);
        setOnboardingData(response.data.onboarding);
        
        // Update state based on API data
        updateStateFromAPI(response.data.onboarding);
        
        // Set partner data from API response
        if (response.data.onboarding.user) {
          setPartnerData({
            partnerStatus: "in_progress",
            currentStep: response.data.onboarding.currentStep || 1,
            completedSteps: getLifecycleSteps().filter(step => step.status === 'completed').map(step => step.id),
            nextAction: "Complete compliance documentation",
            estimatedCompletion: "2-3 weeks",
            user: response.data.onboarding.user
          });
        }
      } else {
        throw new Error(response.message || 'Failed to load onboarding data');
      }
    } catch (error) {
      console.error('Error loading onboarding data:', error);
      setApiError(error.message);
      
      // Fallback to localStorage for now
      loadFromLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // Update state from API data
  const updateStateFromAPI = (data) => {
    if (!data?.stepData) return;

    // Update step completion states
    setNdaAgreed(data.stepData.nda?.completed || false);
    setPartnershipAgreed(data.stepData.partnership?.completed || false);
    setShariyahCompleted(data.stepData.shariyah?.completed || false);
    setLegalCompleted(data.stepData.legal?.completed || false);
    // businessCompleted is derived from onboardingData in hook - no need to set here
    setTechSetupCompleted(data.stepData.techSetup?.completed || false);
    setTrainingCompleted(data.stepData.productDevelopment?.completed || false);
    setLaunchCompleted(data.stepData.launch?.completed || false);

    // Update progress states
    if (data.stepData.techSetup?.progress) {
      setTechSetupProgress(data.stepData.techSetup.progress);
    }
    if (data.stepData.productDevelopment?.progress) {
      setTrainingProgress(data.stepData.productDevelopment.progress);
    }
    if (data.stepData.launch?.progress) {
      setLaunchProgress(data.stepData.launch.progress);
    }

    // Update partner type
    if (data.stepData.shariyah?.partnerType) {
      setPartnerType(data.stepData.shariyah.partnerType);
    }

    // Update uploaded files
    if (data.stepData.shariyah?.files) {
      console.log('Loading shariyah files from API:', data.stepData.shariyah.files);
      const mappedFiles = data.stepData.shariyah.files.map((fileData, index) => {
        const file = {
          id: index + 1,
          name: typeof fileData === 'string' ? fileData : fileData.name,
          s3Key: typeof fileData === 'string' ? null : fileData.s3Key,
          size: typeof fileData === 'string' ? null : fileData.size,
          uploadDate: typeof fileData === 'string' ? new Date().toISOString() : fileData.uploadDate
        };
        console.log(`Mapped shariyah file ${index + 1}:`, file);
        return file;
      });
      setUploadedFiles(mappedFiles);
    }

    // Update legal uploaded files
    if (data.stepData.legal?.files) {
      console.log('Loading legal files from API:', data.stepData.legal.files);
      const mappedFiles = data.stepData.legal.files.map((fileData, index) => {
        const file = {
          id: index + 1,
          name: typeof fileData === 'string' ? fileData : fileData.name,
          s3Key: typeof fileData === 'string' ? null : fileData.s3Key,
          size: typeof fileData === 'string' ? null : fileData.size,
          uploadDate: typeof fileData === 'string' ? new Date().toISOString() : fileData.uploadDate
        };
        console.log(`Mapped legal file ${index + 1}:`, file);
        return file;
      });
      setLegalUploadedFiles(mappedFiles);
    }

    // Business documentation files are loaded by useBusinessDocumentation hook
    // No need to set them here - hook will handle via useEffect when onboardingData changes

    // Update product development uploaded files
    if (data.stepData.productDevelopment?.files) {
      console.log('Loading product development files from API:', data.stepData.productDevelopment.files);
      const mappedFiles = data.stepData.productDevelopment.files.map((fileData, index) => {
        const file = {
          id: fileData.s3Key || `productDevelopment-${index + 1}`,
          name: typeof fileData === 'string' ? fileData : fileData.name,
          s3Key: typeof fileData === 'string' ? null : fileData.s3Key,
          size: typeof fileData === 'string' ? null : fileData.size,
          uploadDate: typeof fileData === 'string' ? new Date().toISOString() : fileData.uploadDate
        };
        console.log(`Mapped product development file ${index + 1}:`, file);
        return file;
      });
      setTrainingUploadedFiles(mappedFiles);
    }
  };

  // Fallback to localStorage
  const loadFromLocalStorage = () => {
    // Check for existing NDA agreement
    const existingNDAAgreement = localStorage.getItem('ndaAgreed') === 'true';
    if (existingNDAAgreement) {
      setNdaAgreed(true);
    }

    // Check for existing Partnership agreement
    const existingPartnershipAgreement = localStorage.getItem('partnershipAgreed') === 'true';
    if (existingPartnershipAgreement) {
      setPartnershipAgreed(true);
    }

    // Check for existing Shariyah certification
    const existingShariyahCertification = localStorage.getItem('shariyahCompleted') === 'true';
    if (existingShariyahCertification) {
      setShariyahCompleted(true);
    }

    // Check for existing Legal compliance
    const existingLegalCompliance = localStorage.getItem('legalCompleted') === 'true';
    if (existingLegalCompliance) {
      setLegalCompleted(true);
    }

    // Check for existing Tech setup
    const existingTechSetup = localStorage.getItem('techSetupCompleted') === 'true';
    if (existingTechSetup) {
      setTechSetupCompleted(true);
    }

    // Load tech setup progress
    const savedTechProgress = localStorage.getItem('techSetupProgress');
    if (savedTechProgress) {
      try {
        const progress = JSON.parse(savedTechProgress);
        setTechSetupProgress(progress);
      } catch (error) {
        console.error('Partner dashboard - Error loading tech setup progress:', error);
      }
    }

    // Check for existing Training completion
    const existingTraining = localStorage.getItem('trainingCompleted') === 'true';
    if (existingTraining) {
      setTrainingCompleted(true);
    }

    // Load training progress
    const savedTrainingProgress = localStorage.getItem('trainingProgress');
    if (savedTrainingProgress) {
      try {
        const progress = JSON.parse(savedTrainingProgress);
        setTrainingProgress(progress);
      } catch (error) {
        console.error('Partner dashboard - Error loading product development progress:', error);
      }
    }

    // Check for existing Launch completion
    const existingLaunch = localStorage.getItem('launchCompleted') === 'true';
    if (existingLaunch) {
      setLaunchCompleted(true);
    }

    // Load launch progress
    const savedLaunchProgress = localStorage.getItem('launchProgress');
    if (savedLaunchProgress) {
      try {
        const progress = JSON.parse(savedLaunchProgress);
        setLaunchProgress(progress);
      } catch (error) {
        console.error('Partner dashboard - Error loading launch progress:', error);
      }
    }

    // Load partner type selection
    const savedPartnerType = localStorage.getItem('partnerType');
    if (savedPartnerType) {
      setPartnerType(savedPartnerType);
    }

    // Load partner data
    loadPartnerData();
  };

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
    debugLog('Partner dashboard - Page loaded');
    
    // Update page title for SEO
    document.title = 'Partner Dashboard - HalalCred';
    
    // Check if user is logged in and is a partner
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const isPartner = localStorage.getItem('isPartner') === 'true';
    
    if (!isLoggedIn || !isPartner) {
      navigate('/partner-signin');
      return;
    }

    // Load onboarding data from API
    loadOnboardingData();
  }, [navigate]);

  // Save partner type when it changes
  useEffect(() => {
    if (partnerType) {
      localStorage.setItem('partnerType', partnerType);
    }
  }, [partnerType]);

  const loadPartnerData = async () => {
    try {
      // Use onboarding data if available, otherwise fallback to mock data
      if (onboardingData?.user) {
        const partnerDataFromAPI = {
          partnerStatus: "in_progress",
          currentStep: onboardingData.currentStep || 1,
          completedSteps: getLifecycleSteps().filter(step => step.status === 'completed').map(step => step.id),
          nextAction: "Complete compliance documentation",
          estimatedCompletion: "2-3 weeks",
          user: onboardingData.user
        };
        setPartnerData(partnerDataFromAPI);
      } else {
        // Fallback to mock data
        const mockData = {
          partnerStatus: "in_progress",
          currentStep: onboardingData?.currentStep || 1,
          completedSteps: getLifecycleSteps().filter(step => step.status === 'completed').map(step => step.id),
          nextAction: "Complete compliance documentation",
          estimatedCompletion: "2-3 weeks"
        };
        setPartnerData(mockData);
      }
    } catch (error) {
      console.error('Partner dashboard - Error loading partner data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('isPartner');
    localStorage.removeItem('partnerStatus');
    navigate('/');
  };

  const handleStepClick = (stepIdOrKey) => {
    console.log('handleStepClick called with:', stepIdOrKey, typeof stepIdOrKey);
    
    // Get step config - accept both numeric ID or key
    const stepConfig = typeof stepIdOrKey === 'number' 
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    
    console.log('stepConfig:', stepConfig);
    
    if (!stepConfig) {
      console.warn('Step config not found for:', stepIdOrKey);
      return;
    }
    
    // Check if step is enabled in configuration
    if (!ConfigHelpers.isStepEnabled(stepConfig.key)) {
      console.warn('Step is not enabled:', stepConfig.key);
      return;
    }
    
    const stepKey = stepConfig.key;
    console.log('Opening modal for step:', stepKey);
    
    // Special handling for NDA step - open modal whether completed or not
    if (stepKey === 'nda') {
      console.log('Opening NDA modal');
      setShowNDATerms(true);
      return;
    }
    
    // Special handling for Setup Meeting step
    if (stepKey === 'meeting') {
      setShowSetupMeeting(true);
      return;
    }
    
    // Special handling for Partnership Agreement step
    if (stepKey === 'partnership') {
      setShowPartnershipTerms(true);
      return;
    }
    
    // Special handling for Training & Resources step
        if (stepKey === 'productDevelopment') {
      // Load resource files when opening the modal
      loadAllResourceFiles();
      setShowTrainingResources(true);
      return;
    }
    
    // Special handling for Shariyah Certification step
    if (stepKey === 'shariyah') {
      setShowShariyahCertification(true);
      return;
    }
    
    // Special handling for Legal Compliance step
    if (stepKey === 'legal') {
      setShowLegalCompliance(true);
      return;
    }
    
    // Special handling for Business Documentation step
    if (stepKey === 'businessDocumentation') {
      setShowBusinessDocumentation(true);
      return;
    }
    
    // Special handling for Tech Setup step
    if (stepKey === 'techSetup') {
      setShowTechSetup(true);
      return;
    }
    
    // Special handling for Launch & Community Engagement step
    if (stepKey === 'launch') {
      setShowLaunchEngagement(true);
      return;
    }
    
    // Default: toggle expanded state
    setExpandedStep(expandedStep === stepIdOrKey ? null : stepIdOrKey);
  };

  const handleNDAAgreement = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        nda: {
          completed: true,
          completedAt: new Date().toISOString()
        }
      };
      
      const response = await api.updateStep(userId, 1, stepData, 2);
      
      if (response.status === 'success') {
        setNdaAgreed(true);
        setShowNDATerms(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
        
        // Keep localStorage as backup
        localStorage.setItem('ndaAgreed', 'true');
        localStorage.setItem('ndaAgreementDate', new Date().toISOString());
      } else {
        throw new Error(response.message || 'Failed to update NDA agreement');
      }
    } catch (error) {
      console.error('Error updating NDA agreement:', error);
      alert('Failed to save agreement. Please try again.');
    }
  };

  const handleCloseNDATerms = () => {
    setShowNDATerms(false);
  };

  const handlePartnershipStepComplete = async (stepData) => {
    console.log('PartnerDashboard - Partnership Step Completed:', stepData);
    console.log('PartnerDashboard - Starting partnership step completion process...');
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const apiStepData = {
        partnership: {
          completed: true,
          completedAt: stepData.timestamp,
          documentPath: stepData.documentPath || stepData.filename,
          partnerName: stepData.partnerName,
          s3Key: stepData.s3Key,
          s3Url: stepData.s3Url,
          uploadError: stepData.uploadError
        }
      };
      
      const response = await api.updateStep(userId, 2, apiStepData, 3);
      
      if (response.status === 'success') {
        // Also save the file to partner_files section if S3 upload was successful
        if (stepData.s3Key) {
          try {
            await api.uploadFile(userId, 2, stepData.filename, stepData.s3Key);
            console.log('Partnership agreement document saved to partner_files section');
          } catch (uploadError) {
            console.warn('Failed to save partnership agreement document to partner_files:', uploadError);
            // Don't fail the entire process if partner_files save fails
          }
        }
        
        console.log('PartnerDashboard - Setting partnership as agreed and closing modal...');
        setPartnershipAgreed(true);
        setShowPartnershipTerms(false);
        console.log('PartnerDashboard - Modal should now be closed');
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
        
        // Keep localStorage as backup
        localStorage.setItem('partnershipAgreed', 'true');
        localStorage.setItem('partnershipAgreementDate', stepData.timestamp);
        localStorage.setItem('partnershipDocumentPath', stepData.documentPath || stepData.filename);
        if (stepData.s3Key) {
          localStorage.setItem('partnershipS3Key', stepData.s3Key);
        }
        if (stepData.s3Url) {
          localStorage.setItem('partnershipS3Url', stepData.s3Url);
        }
        
        console.log('Partnership agreement step completed successfully');
      } else {
        throw new Error(response.message || 'Failed to update partnership agreement');
      }
    } catch (error) {
      console.error('Error updating partnership agreement:', error);
      alert('Failed to save partnership agreement. Please try again.');
    }
  };

  const handleNDAStepComplete = async (stepData) => {
    console.log('NDA Step Completed:', stepData);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const apiStepData = {
        nda: {
          completed: true,
          completedAt: stepData.timestamp,
          documentPath: stepData.documentPath || stepData.filename,
          partnerName: stepData.partnerName,
          s3Key: stepData.s3Key,
          s3Url: stepData.s3Url,
          uploadError: stepData.uploadError
        }
      };
      
      const response = await api.updateStep(userId, 1, apiStepData, 2);
      
      if (response.status === 'success') {
        // Also save the file to partner_files section if S3 upload was successful
        if (stepData.s3Key) {
          try {
            await api.uploadFile(userId, 1, stepData.filename, stepData.s3Key);
            console.log('NDA document saved to partner_files section');
          } catch (uploadError) {
            console.warn('Failed to save NDA document to partner_files:', uploadError);
            // Don't fail the entire process if partner_files save fails
          }
        }
        // Mark NDA as agreed
        setNdaAgreed(true);
        
        // Close the NDA modal
        setShowNDATerms(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
        
        // Keep localStorage as backup
        localStorage.setItem('ndaAgreed', 'true');
        localStorage.setItem('ndaAgreementDate', stepData.timestamp);
        localStorage.setItem('ndaDocumentPath', stepData.documentPath || stepData.filename);
        if (stepData.s3Key) {
          localStorage.setItem('ndaS3Key', stepData.s3Key);
        }
        if (stepData.s3Url) {
          localStorage.setItem('ndaS3Url', stepData.s3Url);
        }
        
        console.log('NDA step completed successfully and saved to database. Ready for next step.');
        
        // Show success notification
        const successNotification = document.createElement('div');
        successNotification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 animate-slide-in';
        successNotification.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <span>✅ Step 1 completed! NDA agreement signed and saved.</span>
        `;
        document.body.appendChild(successNotification);
        setTimeout(() => {
          if (document.body.contains(successNotification)) {
            document.body.removeChild(successNotification);
          }
        }, 5000);
      } else {
        console.error('Failed to save NDA completion to database:', response.message);
        alert('PDF generated but failed to save completion. Please try again.');
      }
    } catch (error) {
      console.error('Error saving NDA completion:', error);
      alert('PDF generated but failed to save completion. Please try again.');
    }
  };

  const handleMeetingStepComplete = async (stepData) => {
    console.log('Meeting Step Completed:', stepData);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const apiStepData = {
        meeting: {
          completed: true,
          completedAt: stepData.timestamp || new Date().toISOString(),
          partnerName: stepData.partnerName,
          partnerEmail: stepData.partnerEmail,
          preferredDate: stepData.preferredDate,
          preferredTime: stepData.preferredTime,
          message: stepData.message
        }
      };
      
      const response = await api.updateStep(userId, 2, apiStepData, 3);
      
      if (response.status === 'success') {
        // Close the meeting modal
        setShowSetupMeeting(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
        
        // Keep localStorage as backup
        localStorage.setItem('meetingRequested', 'true');
        localStorage.setItem('meetingRequestDate', stepData.timestamp || new Date().toISOString());
        
        console.log('Meeting step completed successfully and saved to database.');
        
        // Show success notification
        const successNotification = document.createElement('div');
        successNotification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 animate-slide-in';
        successNotification.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <span>✅ Step 2 completed! Meeting request submitted. We'll contact you soon.</span>
        `;
        document.body.appendChild(successNotification);
        setTimeout(() => {
          if (document.body.contains(successNotification)) {
            document.body.removeChild(successNotification);
          }
        }, 5000);
      } else {
        console.error('Failed to save meeting completion to database:', response.message);
        alert('Meeting request sent but failed to save completion. Please try again.');
      }
    } catch (error) {
      console.error('Error saving meeting completion:', error);
      alert('Meeting request sent but failed to save completion. Please try again.');
    }
  };

  /**
   * Handle viewing the completed NDA document from S3
   * Checks for NDA data and opens the document in a new window
   */
  const handleViewNDADocument = async () => {
    try {
      // Check if NDA is completed
      if (!ndaAgreed) {
        alert('NDA agreement has not been completed yet.');
        return;
      }

      // Get NDA data from onboardingData or localStorage
      const ndaData = onboardingData?.stepData?.nda || null;
      const s3Key = ndaData?.s3Key;
      const s3Url = ndaData?.s3Url;
      const documentPath = ndaData?.documentPath || localStorage.getItem('ndaDocumentPath') || 'NDA Agreement';

      // Validate that we have either s3Key or s3Url
      if (!s3Key && !s3Url) {
        console.error('NDA document - Missing s3Key and s3Url:', { ndaData });
        alert('NDA document file information is not available. The document may not have been properly uploaded.');
        return;
      }

      let fileUrl;

      // Prefer s3Key to generate a signed URL (more secure)
      if (s3Key) {
        try {
          console.log('NDA document - Generating signed URL for s3Key:', s3Key);
          
          // Ensure S3 service is initialized
          if (!s3Service.isReady()) {
            const initialized = await s3Service.initialize();
            if (!initialized) {
              throw new Error('Failed to initialize S3 service');
            }
          }

          // Generate signed URL (valid for 1 hour)
          fileUrl = await s3Service.getSignedUrl(s3Key, { stepId: 1 });
          console.log('NDA document - Generated signed URL successfully');
        } catch (s3Error) {
          console.error('NDA document - Error generating signed URL:', s3Error);
          // Fall back to s3Url if available
          if (s3Url) {
            console.log('NDA document - Falling back to s3Url:', s3Url);
            fileUrl = s3Url;
          } else {
            throw new Error('Unable to access the NDA document. Please try again or contact support.');
          }
        }
      } else if (s3Url) {
        // Use s3Url directly if no s3Key is available
        console.log('NDA document - Using s3Url directly:', s3Url);
        fileUrl = s3Url;
      }

      if (fileUrl) {
        // Open the document in a new window
        console.log('NDA document - Opening document:', documentPath);
        window.open(fileUrl, '_blank');
      } else {
        throw new Error('Unable to generate a valid URL for the NDA document.');
      }
    } catch (error) {
      console.error('NDA document - Error viewing NDA document:', error);
      alert(error.message || 'Error viewing NDA document. Please try again or contact support.');
    }
  };

  const handleViewDocument = (stepIdOrKey) => {
    // Support both numeric ID and step key
    const stepKey = typeof stepIdOrKey === 'string' 
      ? stepIdOrKey 
      : getStepKey(stepIdOrKey);
    
    if (stepKey === 'nda' && ndaAgreed) {
      handleViewNDADocument();
    } else if (stepKey === 'partnership') {
      setShowPartnershipDocument(true);
    }
    // Future: Add handlers for other completed steps
  };

  const handleCloseCompletedDocument = () => {
    setShowCompletedDocument(false);
  };

  const handlePartnershipAgreement = async () => {
    // This function is now handled by the PartnershipAgreementModal
    // The modal will call handlePartnershipStepComplete when the step is completed
    console.log('Partnership agreement modal should be opened');
  };

  const handleClosePartnershipTerms = () => {
    setShowPartnershipTerms(false);
  };

  const handleClosePartnershipDocument = () => {
    setShowPartnershipDocument(false);
  };

  const handleCloseShariyahCertification = () => {
    setShowShariyahCertification(false);
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress('Initializing S3 upload...');
    setUploadStatus('Setting up secure document storage');

    try {
      const uploadedFileData = [];
      const partnerId = partnerData?.companyName || 'demo_partner';

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          setUploadProgress(`Uploading file ${i + 1} of ${files.length}: ${file.name}`);
          setUploadStatus(`Securely storing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          // Upload file to S3
          const uploadPromise = s3Service.uploadFile(file, partnerId, 'shariyah');
          
          // Add timeout warning
          const timeoutId = setTimeout(() => {
            setUploadStatus(`Upload taking longer than expected for ${file.name}...`);
          }, 15000); // 15 seconds
          
          const uploadedFile = await uploadPromise;
          clearTimeout(timeoutId);
          
          uploadedFileData.push(uploadedFile);
          setUploadStatus(`Successfully uploaded ${file.name} to secure storage`);
        } catch (fileError) {
          setUploadStatus(`Failed to upload ${file.name}: ${fileError.message}`);
          alert(`Failed to upload ${file.name}: ${fileError.message}`);
          // Continue with other files even if one fails
        }
      }

      if (uploadedFileData.length > 0) {
        setUploadProgress('Finalizing upload...');
        setUploadStatus(`Successfully uploaded ${uploadedFileData.length} file(s) to secure storage`);
        
        // Update state
        const newFiles = [...uploadedFiles, ...uploadedFileData];
        setUploadedFiles(newFiles);
      } else {
        setUploadStatus('No files were successfully uploaded');
      }
    } catch (error) {
      console.error('Partner dashboard - Error uploading files to S3:', error);
      alert('Error uploading files to secure storage. Please check your credentials and try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      setUploadStatus('');
    }
  };

  const handleS3Upload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress('Step 1: Uploading files to secure storage...');
    setUploadStatus('Uploading to trusted cloud storage');

    try {
      const uploadedFileData = [];
      const userId = localStorage.getItem('userId') || 'demo-user-id';

      // Step 1: Upload all files to S3
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          setUploadProgress(`Step 1: Uploading file ${i + 1} of ${files.length}: ${file.name}`);
          setUploadStatus(`Securely storing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          // Upload file to S3
          const uploadPromise = s3Service.uploadFile(file, userId, 'shariyah');
          
          // Add timeout warning
          const timeoutId = setTimeout(() => {
            console.warn('Partner dashboard - Upload taking longer than expected for file:', file.name);
            setUploadStatus(`Upload taking longer than expected for ${file.name}...`);
          }, 15000); // 15 seconds
          
          const uploadedFile = await uploadPromise;
          clearTimeout(timeoutId);
          
          uploadedFileData.push(uploadedFile);
          setUploadStatus(`Successfully uploaded ${file.name} to secure storage`);
          
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          setUploadStatus(`Failed to upload ${file.name}: ${fileError.message}`);
          alert(`Failed to upload ${file.name}: ${fileError.message}`);
          // Continue with other files even if one fails
        }
      }

      // Step 2: Persist file information in database
      if (uploadedFileData.length > 0) {
        setUploadProgress('Step 2: Persisting file information in database...');
        setUploadStatus('Saving file details to database');
        
        // Update state with uploaded files
        const newFiles = [...uploadedFiles, ...uploadedFileData];
        setUploadedFiles(newFiles);
        
        // Persist file information in database
        try {
          const stepData = {
            shariyah: {
              completed: false,
              files: newFiles.map(file => ({
                name: file.name,
                s3Key: file.s3Key,
                size: file.size,
                uploadDate: file.uploadDate
              })),
              partnerType: partnerType
            }
          };
          
          // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
          const updateResponse = await api.updateStep(userId, 5, stepData, 5);
          
          if (updateResponse.status === 'success') {
            console.log('File information persisted in database successfully');
            
            // Also save each file to partner_files section
            for (const file of uploadedFileData) {
              try {
                await api.uploadFile(userId, 5, file.name, file.s3Key);
                console.log(`Shariyah file ${file.name} saved to partner_files section`);
              } catch (uploadError) {
                console.warn(`Failed to save Shariyah file ${file.name} to partner_files:`, uploadError);
                // Don't fail the entire process if partner_files save fails
              }
            }
            
            setUploadStatus(`Successfully uploaded ${uploadedFileData.length} file(s) and saved to database`);
            
            // Refresh onboarding data to get the latest state
            await loadOnboardingData();
          } else {
            console.error('Failed to persist file information in database');
            setUploadStatus(`Files uploaded but failed to save to database`);
          }
        } catch (updateError) {
          console.error('Error persisting file information:', updateError);
          setUploadStatus(`Files uploaded but failed to save to database`);
        }
        
        console.log('Partner dashboard - Two-step upload process completed');
        console.log('  Total files in state:', newFiles.length);
      } else {
        setUploadStatus('No files were successfully uploaded');
      }
    } catch (error) {
      console.error('Partner dashboard - Error in upload process:', error);
      alert('Error uploading files. Please check your credentials and try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      setUploadStatus('');
    }
  };

  const handleViewFile = async (file) => {
    try {
      setIsViewingFile(true);
      console.log('Attempting to view file:', file);
      
      if (!file) {
        console.error('File object is null or undefined');
        alert('File information is missing. Please try again.');
        return;
      }
      
      if (!file.s3Key) {
        console.error('File s3Key is missing:', file);
        alert('File viewing is not available for this document. The file may not have been properly uploaded.');
        return;
      }
      
      // Generate signed URL for S3 file
      console.log('Generating signed URL for s3Key:', file.s3Key);
      const signedUrl = await s3Service.getSignedUrl(file.s3Key, { stepId: 5 });
      console.log('Generated signed URL:', signedUrl);
      
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Partner dashboard - Error viewing file:', error);
      console.error('File object:', file);
      alert('Error viewing file. Please try again.');
    } finally {
      setIsViewingFile(false);
    }
  };

  const handleCompleteCertification = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        shariyah: {
          completed: true,
          completedAt: new Date().toISOString(),
          files: uploadedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          })),
          partnerType: partnerType
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 5, stepData, 6);
      
      if (response.status === 'success') {
        setShariyahCompleted(true);
        localStorage.setItem('shariyahCompleted', 'true');
        localStorage.setItem('shariyahCompletionDate', new Date().toISOString());
        setShowShariyahCertification(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
        
        console.log('Certification completed successfully');
      } else {
        throw new Error(response.message || 'Failed to complete certification');
      }
    } catch (error) {
      console.error('Error completing certification:', error);
      alert('Failed to complete certification. Please try again.');
    }
  };



  const handleDeleteFile = async (fileId) => {
    const fileToDelete = uploadedFiles.find(file => file.id === fileId);
    
    if (!fileToDelete) {
      console.error('Partner dashboard - File not found for deletion:', fileId);
      return;
    }

    try {
      // Delete from S3 if it's an S3 file
      if (fileToDelete.s3Key) {
        await s3Service.deleteFile(fileToDelete.s3Key);
      }
      
      const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
      setUploadedFiles(updatedFiles);
    } catch (error) {
      console.error('Partner dashboard - Error deleting file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  const handleCloseLegalCompliance = () => {
    setShowLegalCompliance(false);
  };

  const handleLegalFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    console.log('Partner dashboard - Starting legal file upload for', files.length, 'files');
    setIsLegalUploading(true);

    try {
      const uploadedFileData = [];
      const partnerId = partnerData?.companyName || 'demo_partner';

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileStartTime = Date.now();
        
        try {
          console.log('Partner dashboard - Starting legal upload for file:', file.name);
          console.log('  File details:', {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          });
          console.log('  File upload start time:', new Date(fileStartTime).toISOString());
          
          // Upload file to S3
          console.log('Partner dashboard - Calling s3Service.uploadFile for legal document...');
          const uploadPromise = s3Service.uploadFile(file, partnerId, 'legal');
          
          // Add timeout warning
          const timeoutId = setTimeout(() => {
            console.warn('Partner dashboard - Legal upload taking longer than expected for file:', file.name);
            console.warn('  Time elapsed:', Date.now() - fileStartTime, 'ms');
          }, 15000); // 15 seconds
          
          const uploadedFile = await uploadPromise;
          clearTimeout(timeoutId);
          
          const fileEndTime = Date.now();
          const fileUploadDuration = fileEndTime - fileStartTime;
          console.log(`Partner dashboard - Legal file uploaded successfully in ${fileUploadDuration}ms:`, uploadedFile);
          console.log('  File upload end time:', new Date(fileEndTime).toISOString());
          uploadedFileData.push(uploadedFile);
          
        } catch (fileError) {
          const fileErrorTime = Date.now();
          const fileErrorDuration = fileErrorTime - fileStartTime;
          console.error(`Partner dashboard - Error uploading legal file after ${fileErrorDuration}ms:`, file.name);
          console.error('  Error details:', {
            message: fileError.message,
            stack: fileError.stack,
            name: fileError.name,
            code: fileError.code,
            status: fileError.status
          });
          console.error('  Error occurred at:', new Date(fileErrorTime).toISOString());
          
          alert(`Failed to upload ${file.name}: ${fileError.message}`);
          // Continue with other files even if one fails
        }
      }

      if (uploadedFileData.length > 0) {
        console.log('Partner dashboard - Processing uploaded legal files:', uploadedFileData.length);
        
        // Update state
        const newFiles = [...legalUploadedFiles, ...uploadedFileData];
        setLegalUploadedFiles(newFiles);
        
        // Persist file information in database
        try {
          const userId = localStorage.getItem('userId') || 'demo-user-id';
          const stepData = {
            legal: {
              completed: false,
              files: newFiles.map(file => ({
                name: file.name,
                s3Key: file.s3Key,
                size: file.size,
                uploadDate: file.uploadDate
              }))
            }
          };
          
          // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
          const updateResponse = await api.updateStep(userId, 6, stepData, 6);
          
          if (updateResponse.status === 'success') {
            console.log('Legal file information persisted in database successfully');
            
            // Also save each file to partner_files section
            for (const file of uploadedFileData) {
              try {
                await api.uploadFile(userId, 6, file.name, file.s3Key);
                console.log(`Legal file ${file.name} saved to partner_files section`);
              } catch (uploadError) {
                console.warn(`Failed to save legal file ${file.name} to partner_files:`, uploadError);
                // Don't fail the entire process if partner_files save fails
              }
            }
          } else {
            console.error('Failed to persist legal file information in database');
          }
        } catch (updateError) {
          console.error('Error persisting legal file information:', updateError);
        }
        
        console.log('Partner dashboard - Legal files uploaded to S3 successfully');
        console.log('  Total legal files in state:', newFiles.length);
      } else {
        console.log('Partner dashboard - No legal files were successfully uploaded');
      }
    } catch (error) {
      console.error('Partner dashboard - Error uploading legal files to S3:', error);
      console.error('  Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert('Error uploading legal documents to trusted storage. Please check your credentials and try again.');
    } finally {
      setIsLegalUploading(false);
    }
  };

  const handleCompleteLegalCompliance = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        legal: {
          completed: true,
          completedAt: new Date().toISOString(),
          files: legalUploadedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          }))
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 6, stepData, 7);
      
      if (response.status === 'success') {
        setLegalCompleted(true);
        localStorage.setItem('legalCompleted', 'true');
        localStorage.setItem('legalCompletionDate', new Date().toISOString());
        setShowLegalCompliance(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        throw new Error(response.message || 'Failed to complete legal compliance');
      }
    } catch (error) {
      console.error('Error completing legal compliance:', error);
      alert('Failed to complete legal compliance. Please try again.');
    }
  };

  const handleViewLegalFile = async (file) => {
    try {
      setIsViewingFile(true);
      console.log('Attempting to view legal file:', file);
      
      if (!file) {
        console.error('Legal file object is null or undefined');
        alert('File information is missing. Please try again.');
        return;
      }
      
      if (!file.s3Key) {
        console.error('Legal file s3Key is missing:', file);
        alert('File viewing is not available for this document. The file may not have been properly uploaded.');
        return;
      }
      
      // Generate signed URL for S3 file
      console.log('Generating signed URL for legal file s3Key:', file.s3Key);
      const signedUrl = await s3Service.getSignedUrl(file.s3Key, { stepId: 6 });
      console.log('Generated signed URL for legal file:', signedUrl);
      
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Partner dashboard - Error viewing legal file:', error);
      console.error('Legal file object:', file);
      alert('Error viewing file. Please try again.');
    } finally {
      setIsViewingFile(false);
    }
  };

  const handleDeleteLegalFile = async (fileId) => {
    const fileToDelete = legalUploadedFiles.find(file => file.id === fileId);
    
    if (!fileToDelete) {
      console.error('Partner dashboard - Legal file not found for deletion:', fileId);
      return;
    }

    try {
      // Delete from S3 if it's an S3 file
      if (fileToDelete.s3Key) {
        await s3Service.deleteFile(fileToDelete.s3Key);
      }
      
      const updatedFiles = legalUploadedFiles.filter(file => file.id !== fileId);
      setLegalUploadedFiles(updatedFiles);
    } catch (error) {
      console.error('Partner dashboard - Error deleting legal file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  // Business Documentation - use custom hook for all state management
  const {
    businessCompleted,
    showModal: showBusinessDocumentation,
    setShowModal: setShowBusinessDocumentation,
    businessUploadedFiles,
    isBusinessUploading,
    businessUploadProgress,
    businessUploadStatus,
    isViewingBusinessFile,
    handleBusinessFileUpload,
    handleViewBusinessFile,
    handleDeleteBusinessFile,
    handleCompleteBusinessDocumentation
  } = useBusinessDocumentation({
    onboardingData,
    setOnboardingData,
    updateStateFromAPI,
    loadOnboardingData
  });

  const handleCloseTechSetup = () => {
    setShowTechSetup(false);
  };

  const handleTechSetupProgress = async (component) => {
    const updatedProgress = {
      ...techSetupProgress,
      [component]: true
    };
    setTechSetupProgress(updatedProgress);
    localStorage.setItem('techSetupProgress', JSON.stringify(updatedProgress));
    
    // Check if all components are completed
    const allCompleted = Object.values(updatedProgress).every(completed => completed);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        techSetup: {
          completed: allCompleted,
          progress: updatedProgress,
          ...(allCompleted && { completedAt: new Date().toISOString() })
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 7, stepData, allCompleted ? 8 : 7);
      
      if (response.status === 'success') {
        if (allCompleted) {
          setTechSetupCompleted(true);
          localStorage.setItem('techSetupCompleted', 'true');
          localStorage.setItem('techSetupCompletionDate', new Date().toISOString());
        }
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        console.error('Failed to update tech setup progress in API');
      }
    } catch (error) {
      console.error('Error updating tech setup progress:', error);
    }
  };

  const handleCompleteTechSetup = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        techSetup: {
          completed: true,
          completedAt: new Date().toISOString(),
          progress: techSetupProgress
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 7, stepData, 8);
      
      if (response.status === 'success') {
        setTechSetupCompleted(true);
        localStorage.setItem('techSetupCompleted', 'true');
        localStorage.setItem('techSetupCompletionDate', new Date().toISOString());
        setShowTechSetup(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        throw new Error(response.message || 'Failed to complete tech setup');
      }
    } catch (error) {
      console.error('Error completing tech setup:', error);
      alert('Failed to complete tech setup. Please try again.');
    }
  };

  const handleCloseTrainingResources = () => {
    setShowTrainingResources(false);
  };

  const handleTrainingProgress = async (module) => {
    const updatedProgress = {
      ...trainingProgress,
      [module]: true
    };
    setTrainingProgress(updatedProgress);
    localStorage.setItem('trainingProgress', JSON.stringify(updatedProgress));
    
    // Check if all training modules are completed
    const allCompleted = Object.values(updatedProgress).every(completed => completed);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        productDevelopment: {
          completed: allCompleted,
          progress: updatedProgress,
          ...(allCompleted && { completedAt: new Date().toISOString() })
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const trainingStepId = 4; // API step ID for training
      const nextStepId = 5; // Next step is Shariyah
      const response = await api.updateStep(userId, trainingStepId, stepData, allCompleted ? nextStepId : trainingStepId);
      
      if (response.status === 'success') {
        if (allCompleted) {
          setTrainingCompleted(true);
          localStorage.setItem('trainingCompleted', 'true');
          localStorage.setItem('trainingCompletionDate', new Date().toISOString());
        }
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        console.error('Failed to update product development progress in API');
      }
    } catch (error) {
      console.error('Error updating product development progress:', error);
    }
  };

  const handleCompleteTraining = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        productDevelopment: {
          completed: true,
          completedAt: new Date().toISOString(),
          progress: trainingProgress,
          files: trainingUploadedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          }))
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const trainingStepId = 4; // API step ID for training
      const nextStepId = 5; // Next step is Shariyah
      const response = await api.updateStep(userId, trainingStepId, stepData, nextStepId);
      
      if (response.status === 'success') {
        // Also save each file to partner_files section
        for (const file of trainingUploadedFiles) {
          try {
            await api.uploadFile(userId, 4, file.name, file.s3Key); // API step ID 4 for training
            console.log(`Product development file ${file.name} saved to partner_files section`);
          } catch (uploadError) {
            console.warn(`Failed to save training file ${file.name} to partner_files:`, uploadError);
          }
        }
        
        setTrainingCompleted(true);
        localStorage.setItem('trainingCompleted', 'true');
        localStorage.setItem('trainingCompletionDate', new Date().toISOString());
        setShowTrainingResources(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        throw new Error(response.message || 'Failed to complete product development');
      }
    } catch (error) {
      console.error('Error completing product development:', error);
      alert('Failed to complete product development. Please try again.');
    }
  };

  /** Persist product-development (training step) file list to onboarding + partner_files. */
  const persistProductDevelopmentStepFiles = async (newFilesList, batchForPartnerFiles) => {
    const userId = localStorage.getItem('userId') || 'demo-user-id';
    const stepData = {
      productDevelopment: {
        completed: false,
        progress: trainingProgress,
        files: newFilesList.map((file) => ({
          name: file.name,
          s3Key: file.s3Key,
          size: file.size,
          uploadDate: file.uploadDate,
        })),
      },
    };
    const trainingStepId = 4;
    const updateResponse = await api.updateStep(userId, trainingStepId, stepData, trainingStepId);
    if (updateResponse.status !== 'success') {
      throw new Error(updateResponse.message || 'Failed to persist training file information');
    }
    for (const file of batchForPartnerFiles) {
      try {
        await api.uploadFile(userId, 4, file.name, file.s3Key);
      } catch (uploadError) {
        console.warn(`Failed to save training file ${file.name} to partner_files:`, uploadError);
      }
    }
    await loadOnboardingData();
  };

  const handleTrainingTemplateSecureSubmitted = async (uploadResult) => {
    if (!uploadResult?.name || !uploadResult?.s3Key) {
      console.warn('[PartnerDashboard] training template submit: missing upload fields', uploadResult);
      return;
    }
    const normalized = {
      id: uploadResult.s3Key || uploadResult.id,
      name: uploadResult.name,
      s3Key: uploadResult.s3Key,
      size: uploadResult.size ?? 0,
      uploadDate: uploadResult.uploadDate || new Date().toISOString(),
    };
    const newFiles = [...trainingUploadedFiles, normalized];
    setTrainingUploadedFiles(newFiles);
    try {
      await persistProductDevelopmentStepFiles(newFiles, [normalized]);
    } catch (e) {
      console.error('[PartnerDashboard] Failed to persist template submission to document list:', e);
      setTrainingUploadedFiles((prev) => prev.filter((f) => f.s3Key !== normalized.s3Key));
      alert(
        'Your file was stored securely, but it could not be added to your uploaded documents list. Please try again or use Upload Documents.'
      );
    }
  };

  const handleTrainingFileUpload = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setIsTrainingUploading(true);
    setTrainingUploadProgress('Step 1: Uploading files to secure storage...');
    setTrainingUploadStatus('Uploading to trusted cloud storage');

    try {
      const uploadedFileData = [];
      const userId = localStorage.getItem('userId') || 'demo-user-id';

      // Step 1: Upload all files to S3
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        
        try {
          setTrainingUploadProgress(`Step 1: Uploading file ${i + 1} of ${fileArray.length}: ${file.name}`);
          setTrainingUploadStatus(`Securely storing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          
          // Upload file to S3
          const uploadPromise = s3Service.uploadFile(file, userId, 'productDevelopment');
          
          // Add timeout warning
          const timeoutId = setTimeout(() => {
            console.warn('Training file upload taking longer than expected for file:', file.name);
            setTrainingUploadStatus(`Upload taking longer than expected for ${file.name}...`);
          }, 15000); // 15 seconds
          
          const uploadedFile = await uploadPromise;
          clearTimeout(timeoutId);
          
          uploadedFileData.push(uploadedFile);
          setTrainingUploadStatus(`Successfully uploaded ${file.name} to secure storage`);
          
        } catch (fileError) {
          console.error(`Error uploading product development file ${file.name}:`, fileError);
          setTrainingUploadStatus(`Failed to upload ${file.name}: ${fileError.message}`);
          alert(`Failed to upload ${file.name}: ${fileError.message}`);
          // Continue with other files even if one fails
        }
      }

      // Step 2: Persist file information in database
      if (uploadedFileData.length > 0) {
        setTrainingUploadProgress('Step 2: Persisting file information in database...');
        setTrainingUploadStatus('Saving file details to database');
        
        // Update state with uploaded files
        const newFiles = [...trainingUploadedFiles, ...uploadedFileData];
        setTrainingUploadedFiles(newFiles);

        try {
          await persistProductDevelopmentStepFiles(newFiles, uploadedFileData);
          console.log('Product development file information persisted in database successfully');
          setTrainingUploadStatus(`Successfully uploaded ${uploadedFileData.length} file(s) and saved to database`);
        } catch (updateError) {
          console.error('Error persisting training file information:', updateError);
          setTrainingUploadStatus(`Files uploaded but failed to save to database`);
        }
        
        console.log('Product development file upload process completed');
        console.log('  Total files in state:', newFiles.length);
      } else {
        setTrainingUploadStatus('No files were successfully uploaded');
      }
    } catch (error) {
      console.error('Error in product development file upload process:', error);
      alert('Error uploading files. Please check your credentials and try again.');
    } finally {
      setIsTrainingUploading(false);
      setTrainingUploadProgress('');
      setTrainingUploadStatus('');
    }
  };

  const handleViewTrainingFile = async (file) => {
    try {
      setIsViewingTrainingFile(true);
      console.log('Attempting to view product development file:', file);
      
      if (!file) {
        console.error('File object is null or undefined');
        alert('File information is missing. Please try again.');
        return;
      }
      
      if (!file.s3Key) {
        console.error('File s3Key is missing:', file);
        alert('File viewing is not available for this document. The file may not have been properly uploaded.');
        return;
      }
      
      // Generate signed URL for S3 file
      console.log('Generating signed URL for s3Key:', file.s3Key);
      const signedUrl = await s3Service.getSignedUrl(file.s3Key, { stepId: 4 });
      console.log('Generated signed URL:', signedUrl);
      
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing product development file:', error);
      console.error('File object:', file);
      alert('Error viewing file. Please try again.');
    } finally {
      setIsViewingTrainingFile(false);
    }
  };

  const handleDeleteTrainingFile = async (fileId) => {
    const fileToDelete = trainingUploadedFiles.find(file => file.id === fileId || file.s3Key === fileId);
    
    if (!fileToDelete) {
      console.error('Product development file not found for deletion:', fileId);
      return;
    }

    try {
      // Delete from S3 if it's an S3 file
      if (fileToDelete.s3Key) {
        await s3Service.deleteFile(fileToDelete.s3Key);
      }
      
      const updatedFiles = trainingUploadedFiles.filter(file => file.id !== fileId && file.s3Key !== fileId);
      setTrainingUploadedFiles(updatedFiles);
      
      // Update database
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        productDevelopment: {
          completed: false,
          progress: trainingProgress,
          files: updatedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          }))
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const trainingStepId = 4; // API step ID for training
      await api.updateStep(userId, trainingStepId, stepData, trainingStepId);
    } catch (error) {
      console.error('Error deleting product development file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  /**
   * Load files for a specific resource type
   */
  const loadResourceFilesByType = async (resourceType) => {
    try {
      if (!s3Service.isReady()) {
        await s3Service.initialize();
      }
      
      const files = await s3Service.listFiles(`resources/${resourceType}/`);
      
      const filesWithUrls = await Promise.all(files.map(async (file) => {
        try {
          const signedUrl = await s3Service.getSignedUrl(file.key);
          return {
            ...file,
            url: signedUrl
          };
        } catch (error) {
          console.error('Error generating signed URL', { fileKey: file.key, error: error.message });
          return { ...file };
        }
      }));
      
      return filesWithUrls;
    } catch (error) {
      console.error('Error loading resource files', { resourceType, error: error.message });
      return [];
    }
  };

  /**
   * Load all resource files
   */
  const loadAllResourceFiles = useCallback(async () => {
    // Prevent multiple simultaneous loads using ref
    if (isLoadingResourcesRef.current) {
      console.log('Resource files already loading, skipping...');
      return;
    }

    isLoadingResourcesRef.current = true;
    setLoadingResourceFiles(true);
    try {
      const resourceTypes = getAllResourceTypes();
      const loadedFiles = await Promise.all(
        resourceTypes.map(async (type) => ({
          type,
          files: await loadResourceFilesByType(type)
        }))
      );

      // Use functional update to ensure we're working with latest state
      setResourceFiles((prevResourceFiles) => {
        const newResourceFiles = { ...prevResourceFiles };
        loadedFiles.forEach(({ type, files }) => {
          newResourceFiles[type] = files;
        });
        return newResourceFiles;
      });
    } catch (error) {
      console.error('Error loading all resource files', error);
    } finally {
      isLoadingResourcesRef.current = false;
      setLoadingResourceFiles(false);
    }
  }, []); // Empty deps - use ref to track loading state

  /**
   * Handle viewing a resource file
   */
  const handleViewResourceFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  /**
   * Handle downloading a resource file
   */
  const handleDownloadResourceFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const handleCloseLaunchEngagement = () => {
    setShowLaunchEngagement(false);
  };

  const handleLaunchProgress = async (component) => {
    const updatedProgress = {
      ...launchProgress,
      [component]: true
    };
    setLaunchProgress(updatedProgress);
    localStorage.setItem('launchProgress', JSON.stringify(updatedProgress));
    
    // Check if all components are completed
    const allCompleted = Object.values(updatedProgress).every(completed => completed);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        launch: {
          completed: allCompleted,
          progress: updatedProgress,
          ...(allCompleted && { completedAt: new Date().toISOString() })
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 8, stepData, allCompleted ? 8 : 8);
      
      if (response.status === 'success') {
        if (allCompleted) {
          setLaunchCompleted(true);
          localStorage.setItem('launchCompleted', 'true');
          localStorage.setItem('launchCompletionDate', new Date().toISOString());
        }
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        console.error('Failed to update launch progress in API');
      }
    } catch (error) {
      console.error('Error updating launch progress:', error);
    }
  };

  const handleCompleteLaunch = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        launch: {
          completed: true,
          completedAt: new Date().toISOString(),
          progress: launchProgress
        }
      };
      
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch
      const response = await api.updateStep(userId, 8, stepData, 8);
      
      if (response.status === 'success') {
        setLaunchCompleted(true);
        localStorage.setItem('launchCompleted', 'true');
        localStorage.setItem('launchCompletionDate', new Date().toISOString());
        setShowLaunchEngagement(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          // Preserve user data from existing onboardingData
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          setOnboardingData(updatedOnboardingData);
          updateStateFromAPI(updatedOnboardingData);
        }
      } else {
        throw new Error(response.message || 'Failed to complete launch');
      }
    } catch (error) {
      console.error('Error completing launch:', error);
      alert('Failed to complete launch. Please try again.');
    }
  };

  const handleLaunchAction = (actionType) => {
    // In production, this would trigger the actual action
    // For demo, we'll show a message
    alert(`Initiating ${actionType}. In production, this would start the actual process.`);
  };

  const handleClearDashboardData = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      
      // Reset step data back to step 1 in the API
      const resetStepData = {
        nda: {
          completed: false
        },
        partnership: {
          completed: false
        },
        shariyah: {
          completed: false,
          files: [],
          partnerType: null
        },
        legal: {
          completed: false,
          files: []
        },
        techSetup: {
          completed: false,
          progress: {
            loanApplication: false,
            creditScoring: false,
            paymentSystem: false,
            auditTrail: false
          }
        },
        training: {
          completed: false,
          progress: {
            islamicFinance101: false,
            salesTraining: false,
            systemTraining: false,
            resourceAccess: false
          }
        },
        launch: {
          completed: false,
          progress: {
            pilotProgram: false,
            pricingStrategy: false,
            transparencyTools: false,
            communityOutreach: false
          }
        }
      };
      
      // Update API to reset to step 1
      const response = await api.updateStep(userId, 1, resetStepData, 1);
      
      if (response.status === 'success') {
        console.log('Dashboard data reset successfully in API');
        
        // Clear localStorage as backup
        clearDashboardData();
        
        // Reset all state variables
        setNdaAgreed(false);
        setPartnershipAgreed(false);
        setShariyahCompleted(false);
        setLegalCompleted(false);
        setTechSetupCompleted(false);
        setTechSetupProgress({
          loanApplication: false,
          creditScoring: false,
          paymentSystem: false,
          auditTrail: false
        });
        setTrainingCompleted(false);
        setTrainingProgress({
          islamicFinance101: false,
          salesTraining: false,
          systemTraining: false,
          resourceAccess: false
        });
        setLaunchCompleted(false);
        setLaunchProgress({
          pilotProgram: false,
          pricingStrategy: false,
          transparencyTools: false,
          communityOutreach: false
        });
        setUploadedFiles([]);
        setLegalUploadedFiles([]);
        setExpandedStep(null);
        setPartnerType('');
        
        // Update onboarding data from API response
        if (response.data.onboarding) {
          setOnboardingData(response.data.onboarding);
          updateStateFromAPI(response.data.onboarding);
        }

        alert('Dashboard data cleared successfully! All progress has been reset to step 1.');
      } else {
        throw new Error(response.message || 'Failed to reset dashboard data');
      }
    } catch (error) {
      console.error('Error clearing dashboard data:', error);
      alert('Failed to clear dashboard data. Please try again.');
    }
  };

  const renderStepDetails = (step) => {
    const isExpanded = expandedStep === step.id;
    
    return (
      <motion.div
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <Card className="mt-4 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Details Section */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#1a4a1a] mb-2">Summary</h4>
                  <p className="text-[#1a4a1a]/80">{step.details.summary}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#1a4a1a] mb-2">
                    {step.status === 'completed' ? 'Terms & Conditions' : 
                     step.status === 'in_progress' ? 'Requirements' : 
                     step.title === 'Setup Halal Finance Processing Infra' ? 'Components' : 'Training Modules'}
                  </h4>
                  <ul className="space-y-2">
                    {(step.details.terms || step.details.requirements || step.details.components || step.details.trainingModules)?.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-[#1a4a1a]/80 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Status Information */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-emerald-200">
                  {step.completedDate && (
                    <div>
                      <h5 className="font-medium text-[#1a4a1a] text-sm">Completed Date</h5>
                      <p className="text-[#1a4a1a]/70 text-sm">{step.completedDate}</p>
                    </div>
                  )}
                  {step.approvedBy && (
                    <div>
                      <h5 className="font-medium text-[#1a4a1a] text-sm">Approved By</h5>
                      <p className="text-[#1a4a1a]/70 text-sm">{step.approvedBy}</p>
                    </div>
                  )}
                  {step.startedDate && (
                    <div>
                      <h5 className="font-medium text-[#1a4a1a] text-sm">Started Date</h5>
                      <p className="text-[#1a4a1a]/70 text-sm">{step.startedDate}</p>
                    </div>
                  )}
                  {step.estimatedCompletion && (
                    <div>
                      <h5 className="font-medium text-[#1a4a1a] text-sm">Estimated Completion</h5>
                      <p className="text-[#1a4a1a]/70 text-sm">{step.estimatedCompletion}</p>
                    </div>
                  )}
                  {step.estimatedStart && (
                    <div>
                      <h5 className="font-medium text-[#1a4a1a] text-sm">Estimated Start</h5>
                      <p className="text-[#1a4a1a]/70 text-sm">{step.estimatedStart}</p>
                    </div>
                  )}
                </div>
              </div>


            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-emerald-200">
              {step.status === 'completed' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => handleViewDocument(step.id)}
                >
                  View Document
                </Button>
              )}
              {step.status === 'in_progress' && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Continue Process
                </Button>
              )}
              {step.status === 'pending' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-400 text-gray-600 hover:bg-gray-50"
                  disabled
                >
                  Not Started Yet
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  const renderStepIcon = (step) => {
    const IconComponent = step.icon;
    const isCompleted = step.status === 'completed';
    const isInProgress = step.status === 'in_progress';
    
    if (isCompleted) {
      return <CheckCircle className="w-6 h-6 text-white" />;
    } else if (isInProgress) {
      return <Clock className="w-6 h-6 text-white" />;
    } else {
      return <IconComponent className="w-6 h-6 text-white" />;
    }
  };

  const getStepStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      default:
        return 'Not Started';
    }
  };

  const getStepActionLabel = (stepKey) => {
    const labels = {
      nda: 'Review & Sign',
      meeting: 'Schedule Meeting',
      partnership: 'Review & Sign',
      productDevelopment: 'Open Templates & Resources',
      businessDocumentation: 'Upload Business Documents',
      shariyah: 'Manage Certifications',
      legal: 'Upload Legal Documents',
      techSetup: 'Start Setup',
      launch: 'View Launch',
    };
    return labels[stepKey] || 'Open';
  };

  // Launch & Community Engagement Modal
  const renderLaunchEngagementModal = () => (
    <AnimatePresence>
      {showLaunchEngagement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseLaunchEngagement}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Launch & Community Engagement</h2>
                </div>
                <button
                  onClick={handleCloseLaunchEngagement}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                <div className={`p-4 rounded-lg border ${launchCompleted ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {launchCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                    <h3 className={`font-semibold ${launchCompleted ? 'text-green-800' : 'text-yellow-800'}`}>
                      Status: {launchCompleted ? 'Completed' : 'In Progress'}
                    </h3>
                  </div>
                  <p className={`text-sm ${launchCompleted ? 'text-green-700' : 'text-yellow-700'}`}>
                    {launchCompleted 
                      ? 'Launch completed successfully. Your halal finance services are now active in the community.'
                      : 'Complete the launch components to engage with your community and start offering halal services.'
                    }
                  </p>
                </div>

                {/* Launch Components Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Launch Components</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    
                    {/* Pilot Program */}
                    <div className={`p-4 rounded-lg border ${launchProgress.pilotProgram ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Pilot Program</h4>
                        {launchProgress.pilotProgram && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Community partnerships and educational workshops
                      </p>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Local mosque partnerships</li>
                        <li>• Muslim chambers of commerce</li>
                        <li>• Community group collaborations</li>
                        <li>• Halal finance workshops</li>
                      </ul>
                      {!launchProgress.pilotProgram && (
                        <div className="space-y-2">
                          <Button
                            size="sm"
                            onClick={() => handleLaunchAction('Pilot Program Setup')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                          >
                            Setup Program
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLaunchProgress('pilotProgram')}
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          >
                            Mark Complete
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Pricing Strategy */}
                    <div className={`p-4 rounded-lg border ${launchProgress.pricingStrategy ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Pricing Strategy</h4>
                        {launchProgress.pricingStrategy && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Competitive riba-free pricing and alternative credit scoring
                      </p>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Cost-based profit margins</li>
                        <li>• No compounding interest</li>
                        <li>• Alternative credit assessment</li>
                        <li>• Rental and utility history</li>
                      </ul>
                      {!launchProgress.pricingStrategy && (
                        <div className="space-y-2">
                          <Button
                            size="sm"
                            onClick={() => handleLaunchAction('Pricing Strategy Setup')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                          >
                            Configure Pricing
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLaunchProgress('pricingStrategy')}
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          >
                            Mark Complete
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Transparency Tools */}
                    <div className={`p-4 rounded-lg border ${launchProgress.transparencyTools ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Transparency Tools</h4>
                        {launchProgress.transparencyTools && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Client portal and fee transparency systems
                      </p>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Asset ownership status</li>
                        <li>• Transparent fee breakdown</li>
                        <li>• No hidden charges</li>
                        <li>• Charitable payment tracking</li>
                      </ul>
                      {!launchProgress.transparencyTools && (
                        <div className="space-y-2">
                          <Button
                            size="sm"
                            onClick={() => handleLaunchAction('Transparency Tools Setup')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                          >
                            Setup Portal
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLaunchProgress('transparencyTools')}
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          >
                            Mark Complete
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Community Outreach */}
                    <div className={`p-4 rounded-lg border ${launchProgress.communityOutreach ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Community Outreach</h4>
                        {launchProgress.communityOutreach && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Local engagement and halal finance education
                      </p>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Local community events</li>
                        <li>• Halal finance education</li>
                        <li>• Trust building initiatives</li>
                        <li>• Service awareness campaigns</li>
                      </ul>
                      {!launchProgress.communityOutreach && (
                        <div className="space-y-2">
                          <Button
                            size="sm"
                            onClick={() => handleLaunchAction('Community Outreach Setup')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                          >
                            Start Outreach
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLaunchProgress('communityOutreach')}
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          >
                            Mark Complete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Launch Strategy Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Launch Strategy</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium mb-2">Community Partnerships</h4>
                        <ul className="text-sm space-y-1 text-gray-600">
                          <li>• Local mosque collaborations</li>
                          <li>• Muslim business networks</li>
                          <li>• Community center partnerships</li>
                          <li>• Educational institution ties</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Service Launch</h4>
                        <ul className="text-sm space-y-1 text-gray-600">
                          <li>• Gradual market entry</li>
                          <li>• Pilot customer onboarding</li>
                          <li>• Feedback collection systems</li>
                          <li>• Service refinement process</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transparency Features */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Transparency Features</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                      <h4 className="font-medium text-emerald-800 mb-2">Client Portal</h4>
                      <ul className="text-sm space-y-1 text-emerald-700">
                        <li>• Real-time loan status</li>
                        <li>• Asset ownership tracking</li>
                        <li>• Payment history</li>
                        <li>• Document access</li>
                      </ul>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                      <h4 className="font-medium text-emerald-800 mb-2">Fee Transparency</h4>
                      <ul className="text-sm space-y-1 text-emerald-700">
                        <li>• Clear fee breakdown</li>
                        <li>• No hidden charges</li>
                        <li>• Profit margin disclosure</li>
                        <li>• Cost-based pricing</li>
                      </ul>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                      <h4 className="font-medium text-emerald-800 mb-2">Charitable Tracking</h4>
                      <ul className="text-sm space-y-1 text-emerald-700">
                        <li>• Late payment donations</li>
                        <li>• Community impact</li>
                        <li>• Transparency reports</li>
                        <li>• Beneficiary tracking</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Completion Section */}
                {!launchCompleted && Object.values(launchProgress).every(completed => completed) && (
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">Launch Ready</h4>
                    <p className="text-sm text-emerald-700 mb-4">
                      All launch components have been completed successfully. Your halal finance services are ready to go live in the community.
                    </p>
                    <Button
                      onClick={handleCompleteLaunch}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Complete Launch
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {launchCompleted && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Launch Completed Successfully</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Your launch has been completed on {localStorage.getItem('launchCompletionDate') ? new Date(localStorage.getItem('launchCompletionDate')).toLocaleDateString() : 'Not available'}. 
                      Your halal finance services are now active and serving the community.
                    </p>
                    <div className="space-y-2 text-sm text-green-700">
                      <p>✅ Community partnerships established</p>
                      <p>✅ Pricing strategy implemented</p>
                      <p>✅ Transparency tools operational</p>
                      <p>✅ Community outreach active</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {launchCompleted 
                    ? 'Launch completed successfully'
                    : `${Object.values(launchProgress).filter(completed => completed).length}/4 components completed`
                  }
                </p>
                <Button
                  onClick={handleCloseLaunchEngagement}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Training & Resources Modal - Now using separate component
  const renderTrainingResourcesModal = () => (
    <TrainingResourcesModal
      isOpen={showTrainingResources}
      onClose={handleCloseTrainingResources}
      trainingCompleted={trainingCompleted}
      trainingProgress={trainingProgress}
      resourceFiles={resourceFiles}
      loadingResourceFiles={loadingResourceFiles}
      onTrainingProgress={handleTrainingProgress}
      onCompleteTraining={handleCompleteTraining}
      onViewResourceFile={handleViewResourceFile}
      onDownloadResourceFile={handleDownloadResourceFile}
      onLoadResourceFiles={loadAllResourceFiles}
      trainingUploadedFiles={trainingUploadedFiles}
      isTrainingUploading={isTrainingUploading}
      trainingUploadProgress={trainingUploadProgress}
      trainingUploadStatus={trainingUploadStatus}
      onTrainingFileUpload={handleTrainingFileUpload}
      onViewTrainingFile={handleViewTrainingFile}
      onDeleteTrainingFile={handleDeleteTrainingFile}
      isViewingTrainingFile={isViewingTrainingFile}
      onTrainingTemplateSecureSubmitted={handleTrainingTemplateSecureSubmitted}
    />
  );

  // Business Documentation Modal - Using separate component
  const renderBusinessDocumentationModal = () => (
    <BusinessDocumentationModal
      isOpen={showBusinessDocumentation}
      onClose={() => setShowBusinessDocumentation(false)}
      businessCompleted={businessCompleted}
      businessUploadedFiles={businessUploadedFiles}
      isBusinessUploading={isBusinessUploading}
      businessUploadProgress={businessUploadProgress}
      businessUploadStatus={businessUploadStatus}
      onBusinessFileUpload={handleBusinessFileUpload}
      onViewBusinessFile={handleViewBusinessFile}
      onDeleteBusinessFile={handleDeleteBusinessFile}
      onCompleteBusinessDocumentation={handleCompleteBusinessDocumentation}
      isViewingBusinessFile={isViewingBusinessFile}
    />
  );

  // Tech Setup Modal
  const renderTechSetupModal = () => (
    <AnimatePresence>
      {showTechSetup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseTechSetup}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Settings className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Halal Finance Technology Setup</h2>
                </div>
                <button
                  onClick={handleCloseTechSetup}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                <div className={`p-4 rounded-lg border ${techSetupCompleted ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {techSetupCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                    <h3 className={`font-semibold ${techSetupCompleted ? 'text-green-800' : 'text-yellow-800'}`}>
                      Status: {techSetupCompleted ? 'Completed' : 'In Progress'}
                    </h3>
                  </div>
                  <p className={`text-sm ${techSetupCompleted ? 'text-green-700' : 'text-yellow-700'}`}>
                    {techSetupCompleted 
                      ? 'Your halal finance technology infrastructure has been successfully implemented. All systems are operational.'
                      : 'Please complete the technology setup components to enable halal loan processing capabilities.'
                    }
                  </p>
                </div>

                {/* Tech Components Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Technology Components</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    
                    {/* Loan Application System */}
                    <div className={`p-4 rounded-lg border ${techSetupProgress.loanApplication ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Halal Loan Application System</h4>
                        {techSetupProgress.loanApplication && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Murabaha and Ijarah loan types</li>
                        <li>• Asset-based financing workflows</li>
                        <li>• Transparent profit calculations</li>
                        <li>• Customer application portal</li>
                      </ul>
                      {!techSetupProgress.loanApplication && (
                        <Button
                          size="sm"
                          onClick={() => handleTechSetupProgress('loanApplication')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Setup Complete
                        </Button>
                      )}
                    </div>

                    {/* Credit Scoring System */}
                    <div className={`p-4 rounded-lg border ${techSetupProgress.creditScoring ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Riba-Free Credit Scoring</h4>
                        {techSetupProgress.creditScoring && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Islamic credit assessment</li>
                        <li>• Character-based evaluation</li>
                        <li>• Income verification systems</li>
                        <li>• Risk assessment tools</li>
                      </ul>
                      {!techSetupProgress.creditScoring && (
                        <Button
                          size="sm"
                          onClick={() => handleTechSetupProgress('creditScoring')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Setup Complete
                        </Button>
                      )}
                    </div>

                    {/* Payment System */}
                    <div className={`p-4 rounded-lg border ${techSetupProgress.paymentSystem ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Transparent Payment Processing</h4>
                        {techSetupProgress.paymentSystem && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• ACH payment integrations</li>
                        <li>• Riba-free transaction processing</li>
                        <li>• Transparent fee structures</li>
                        <li>• Payment scheduling system</li>
                      </ul>
                      {!techSetupProgress.paymentSystem && (
                        <Button
                          size="sm"
                          onClick={() => handleTechSetupProgress('paymentSystem')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Setup Complete
                        </Button>
                      )}
                    </div>

                    {/* Audit Trail System */}
                    <div className={`p-4 rounded-lg border ${techSetupProgress.auditTrail ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Islamic Audit Trail</h4>
                        {techSetupProgress.auditTrail && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <ul className="text-sm space-y-1 text-gray-600 mb-3">
                        <li>• Asset ownership tracking</li>
                        <li>• Scholar approval records</li>
                        <li>• Transaction audit logs</li>
                      </ul>
                      {!techSetupProgress.auditTrail && (
                        <Button
                          size="sm"
                          onClick={() => handleTechSetupProgress('auditTrail')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Setup Complete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Integration Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">System Integrations</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium mb-2">Core Banking Systems</h4>
                        <ul className="text-sm space-y-1 text-gray-600">
                          <li>• Mambu or Apiture core banking</li>
                          <li>• Custom profit calculation modules</li>
                          <li>• Asset acquisition partnerships</li>
                          <li>• Vendor management systems</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">External Integrations</h4>
                        <ul className="text-sm space-y-1 text-gray-600">
                          <li>• Plaid/Finicity for ACH transfers</li>
                          <li>• ComplyAdvantage for KYC/AML</li>
                          <li>• Payment gateway APIs</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Completion Section */}
                {!techSetupCompleted && Object.values(techSetupProgress).every(completed => completed) && (
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">All Components Ready</h4>
                    <p className="text-sm text-emerald-700 mb-4">
                      All technology components have been successfully configured. Your halal finance platform is ready for operation.
                    </p>
                    <Button
                      onClick={handleCompleteTechSetup}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Complete Tech Setup
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {techSetupCompleted && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Technology Setup Completed</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Your halal finance technology infrastructure has been completed on {localStorage.getItem('techSetupCompletionDate') ? new Date(localStorage.getItem('techSetupCompletionDate')).toLocaleDateString() : 'Not available'}. 
                      All systems are operational and ready for halal loan processing.
                    </p>
                    <div className="space-y-2 text-sm text-green-700">
                      <p>Halal loan application system operational</p>
                      <p>Riba-free credit scoring active</p>
                      <p>Transparent payment processing ready</p>
                      <p>Islamic audit trail system configured</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {techSetupCompleted 
                    ? 'Technology setup completed successfully'
                    : `${Object.values(techSetupProgress).filter(completed => completed).length}/4 components configured`
                  }
                </p>
                <Button
                  onClick={handleCloseTechSetup}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Legal Compliance Modal
  const renderLegalComplianceModal = () => (
    <AnimatePresence>
      {showLegalCompliance && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseLegalCompliance}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Scale className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Legal Compliance Verification</h2>
                </div>
                <button
                  onClick={handleCloseLegalCompliance}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                <div className={`p-4 rounded-lg border ${legalCompleted ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {legalCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                    <h3 className={`font-semibold ${legalCompleted ? 'text-green-800' : 'text-yellow-800'}`}>
                      Status: {legalCompleted ? 'Completed' : 'Pending'}
                    </h3>
                  </div>
                  <p className={`text-sm ${legalCompleted ? 'text-green-700' : 'text-yellow-700'}`}>
                    {legalCompleted 
                      ? 'Your legal compliance verification has been completed successfully. All documents have been reviewed and approved.'
                      : 'Please upload the required legal documents for regulatory verification. Our legal team will review your submission.'
                    }
                  </p>
                </div>

                {/* Requirements Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Required Legal Documents</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Regulatory Documentation</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Financial services licenses</li>
                        <li>• Regulatory registrations</li>
                        <li>• Business permits and authorizations</li>
                        <li>• Industry-specific certifications</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Operational Documentation</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• AML/KYC policies and procedures</li>
                        <li>• Risk management frameworks</li>
                        <li>• Internal control systems</li>
                        <li>• Audit and reporting procedures</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* File Upload Section */}
                {!legalCompleted && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Upload Legal Documents</h3>
                    <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center">
                      <div className="text-2xl mb-3">📋</div>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload your legal documents for regulatory verification. 
                        Supported formats: PDF, DOC, DOCX, RTF, XLS, XLSX, ODT, ODS, TXT, CSV, JPG, PNG (Max 10MB each)
                      </p>
                      <p className="text-xs text-emerald-600 mb-4">
                        💡 Documents are stored securely in trusted cloud storage with encryption and access controls.
                      </p>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png"
                        onChange={handleLegalFileUpload}
                        disabled={isLegalUploading}
                        className="hidden"
                        id="legal-file-upload"
                      />
                      <label
                        htmlFor="legal-file-upload"
                        className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                          isLegalUploading 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        {isLegalUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Uploading to Secure Storage...
                          </>
                        ) : (
                          'Upload Documents'
                        )}
                      </label>
                    </div>
                  </div>
                )}

                {/* Uploaded Files Section */}
                {legalUploadedFiles.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Uploaded Legal Documents</h3>
                    <div className="space-y-3">
                      {legalUploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">☁️</div>
                            <div>
                              <p className="font-medium text-sm">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(file.uploadDate).toLocaleDateString()} • {(file.size / 1024 / 1024).toFixed(2)} MB
                                <span className="ml-2 text-emerald-600">• Secure Storage</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLegalFile(file)}
                              disabled={isViewingFile}
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isViewingFile ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                                  Loading...
                                </>
                              ) : (
                                'View Document'
                              )}
                            </Button>
                            {!legalCompleted && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteLegalFile(file.id)}
                                className="border-red-600 text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion Section */}
                {!legalCompleted && legalUploadedFiles.length > 0 && (
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">Ready to Submit</h4>
                    <p className="text-sm text-emerald-700 mb-4">
                      You have uploaded {legalUploadedFiles.length} legal document(s). Review your files and click 
                      "Complete Verification" when ready to submit for legal review.
                    </p>
                    <Button
                      onClick={handleCompleteLegalCompliance}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Complete Verification
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {legalCompleted && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Legal Verification Completed</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Your legal compliance verification has been completed on {localStorage.getItem('legalCompletionDate') ? new Date(localStorage.getItem('legalCompletionDate')).toLocaleDateString() : 'Not available'}. 
                      All documents have been reviewed and approved by our legal team.
                    </p>
                    <div className="space-y-2 text-sm text-green-700">
                      <p>✅ Legal documents reviewed and approved</p>
                      <p>✅ Regulatory requirements verified</p>
                      <p>✅ Operational procedures validated</p>
                      <p>✅ Partnership ready for next phase</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {legalCompleted 
                    ? 'Legal verification completed successfully'
                    : `${legalUploadedFiles.length} legal document(s) uploaded`
                  }
                </p>
                <Button
                  onClick={handleCloseLegalCompliance}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Shariyah Certification Modal - Using separate component (certificates issued by HalalCred admin)
  const renderShariyahCertificationModal = () => (
    <ShariyahCertificationModal
      isOpen={showShariyahCertification}
      onClose={handleCloseShariyahCertification}
      shariyahCompleted={shariyahCompleted}
      uploadedFiles={uploadedFiles}
      isViewingFile={isViewingFile}
      completedAt={onboardingData?.stepData?.shariyah?.completedAt}
      onViewFile={handleViewFile}
      onDownloadFile={handleViewFile}
    />
  );

  // Partnership Agreement Document Modal
  const renderPartnershipDocumentModal = () => (
    <AnimatePresence>
      {showPartnershipDocument && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleClosePartnershipDocument}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Completed Partnership Agreement</h2>
                </div>
                <button
                  onClick={handleClosePartnershipDocument}
                  className="text-white hover:text-green-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Agreement Status */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-green-800">Partnership Status: Active</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-green-700">Agreement Date:</span>
                      <p className="text-green-600">{localStorage.getItem('partnershipAgreementDate') ? new Date(localStorage.getItem('partnershipAgreementDate')).toLocaleDateString() : 'Not available'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">Partnership Type:</span>
                      <p className="text-green-600">Strategic Partnership</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Strategic Partnership Agreement</h3>
                  <p className="text-sm leading-relaxed mb-4">
                    This agreement establishes a strategic partnership between HalalCred and our trusted partners 
                    to provide halal financial services based on mutual trust, fair practices, and shared success.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">1. Partnership Objectives</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Both parties commit to working together to achieve the following objectives:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Provide accessible halal loan services to communities</li>
                    <li>• Build trust through transparent financial practices</li>
                    <li>• Create sustainable business growth opportunities</li>
                    <li>• Support financial inclusion with ethical principles</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">2. Revenue Sharing and Financial Terms</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Our partnership operates under fair and transparent financial arrangements:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Fair revenue sharing based on contribution and value</li>
                    <li>• Transparent fee structures with no hidden costs</li>
                    <li>• Riba-free financial arrangements and transactions</li>
                    <li>• Regular financial reporting and accountability</li>
                    <li>• Mutual benefit and growth sharing</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3. Business Development and Marketing</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Both parties agree to collaborate on business development activities:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Joint marketing campaigns and initiatives</li>
                    <li>• Shared customer acquisition strategies</li>
                    <li>• Collaborative product development</li>
                    <li>• Mutual referral programs</li>
                    <li>• Brand alignment and messaging</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">4. Technology and Infrastructure</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Technology integration and infrastructure sharing:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Secure data sharing and integration</li>
                    <li>• Platform access and technical support</li>
                    <li>• Training and knowledge transfer</li>
                    <li>• Regular system updates and maintenance</li>
                    <li>• Disaster recovery and business continuity</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">5. Quality Standards and Service</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Both parties commit to maintaining high service standards:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Professional customer service excellence</li>
                    <li>• Ethical business practices and integrity</li>
                    <li>• Regular quality assessments and improvements</li>
                    <li>• Customer feedback and satisfaction monitoring</li>
                    <li>• Continuous learning and development</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">6. Dispute Resolution and Governance</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Fair and transparent processes for resolving any disputes:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Open communication and dialogue first</li>
                    <li>• Professional mediation when needed</li>
                    <li>• Fair arbitration processes</li>
                    <li>• Regular partnership reviews and assessments</li>
                    <li>• Continuous improvement and adaptation</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">7. Partnership Duration and Renewal</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    This partnership agreement is established for an initial period of two years, 
                    with automatic renewal options based on mutual satisfaction and performance metrics. 
                    Both parties may review and renegotiate terms annually.
                  </p>
                </div>

                {/* Partnership Confirmation */}
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 mb-2">Partnership Confirmation</h4>
                  <div className="space-y-2 text-sm text-emerald-700">
                    <p>✅ Strategic partnership agreement has been established</p>
                    <p>✅ Terms and conditions are understood and accepted</p>
                    <p>✅ Commitment to mutual growth and success confirmed</p>
                    <p>✅ Partnership foundation for halal services established</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleClosePartnershipDocument}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Close Document
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Completed Agreement Document Modal
  const renderCompletedDocumentModal = () => (
    <AnimatePresence>
      {showCompletedDocument && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseCompletedDocument}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Completed Agreement</h2>
                </div>
                <button
                  onClick={handleCloseCompletedDocument}
                  className="text-white hover:text-green-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Agreement Status */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-green-800">Agreement Status: Completed</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-green-700">Agreement Date:</span>
                      <p className="text-green-600">{localStorage.getItem('ndaAgreementDate') ? new Date(localStorage.getItem('ndaAgreementDate')).toLocaleDateString() : 'Not available'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">Status:</span>
                      <p className="text-green-600">Active</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Partnership Trust Agreement</h3>
                  <p className="text-sm leading-relaxed mb-4">
                    This agreement establishes a foundation of trust and mutual respect between HalalCred and our trusted partners. 
                    We believe in building relationships based on transparency, fairness, and shared values.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">1. Confidentiality Commitment</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Both parties commit to maintaining the confidentiality of all shared business information, 
                    customer data, and proprietary knowledge. This includes but is not limited to:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Business strategies and financial information</li>
                    <li>• Customer data and personal information</li>
                    <li>• Technology platforms and proprietary systems</li>
                    <li>• Marketing materials and business processes</li>
                    <li>• Partnership discussions and negotiations</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">2. Trust and Transparency</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Our partnership is built on the principles of trust and transparency. We commit to:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Open and honest communication</li>
                    <li>• Fair and just business practices</li>
                    <li>• Protection of shared interests</li>
                    <li>• Respect for each other's business boundaries</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3. Data Protection and Privacy</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    We are committed to protecting the privacy and security of all information shared between us:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Secure handling of customer data</li>
                    <li>• Compliance with privacy regulations</li>
                    <li>• Limited access to sensitive information</li>
                    <li>• Regular security assessments</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">4. Fair Business Practices</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Our partnership operates under fair and ethical business principles:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Riba-free financial arrangements</li>
                    <li>• Transparent fee structures</li>
                    <li>• Fair dispute resolution processes</li>
                    <li>• Mutual benefit and growth opportunities</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">5. Partnership Responsibilities</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Both parties agree to:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 mb-3">
                    <li>• Maintain professional standards in all interactions</li>
                    <li>• Provide halal loan services with integrity</li>
                    <li>• Support each other's business objectives</li>
                    <li>• Regular communication and updates</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">6. Agreement Duration</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    This agreement remains in effect for the duration of our partnership and for a period of 
                    two years following its termination, ensuring continued protection of shared information.
                  </p>
                </div>

                {/* Agreement Signature */}
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 mb-2">Agreement Confirmation</h4>
                  <div className="space-y-2 text-sm text-emerald-700">
                    <p>✅ Agreement has been reviewed and accepted</p>
                    <p>✅ Terms and conditions are understood</p>
                    <p>✅ Commitment to trust and confidentiality confirmed</p>
                    <p>✅ Partnership foundation established</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleCloseCompletedDocument}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Close Document
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Partnership Agreement Terms Modal
  const renderPartnershipTermsModal = () => {
    // Get partner name from login response (partnerInfo) or fallback to onboarding data
    const partnerName = getPartnerName();
    
    // Get existing partnership document data - only if partnership is actually completed
    const partnershipData = onboardingData?.stepData?.partnership;
    const isPartnershipCompleted = partnershipData?.completed === true;
    
    const existingPartnershipDocument = isPartnershipCompleted && partnershipData ? {
      filename: partnershipData.documentPath || partnershipData.filename || 'Partnership_Agreement.pdf',
      completedAt: partnershipData.completedAt,
      s3Key: partnershipData.s3Key,
      s3Url: partnershipData.s3Url
    } : null;

    return (
      <PartnershipAgreementModal
        isOpen={showPartnershipTerms}
        onClose={handleClosePartnershipTerms}
        partnerName={partnerName}
        onStepComplete={handlePartnershipStepComplete}
        existingDocument={existingPartnershipDocument}
      />
    );
  };

  // NDA Terms and Conditions Modal
  const renderNDATermsModal = () => {
    // Get partner name from login response (partnerInfo) or fallback to onboarding data
    const partnerName = getPartnerName();
    
    // Debug partner data
    console.log('NDA Modal - Full onboardingData:', onboardingData);
    console.log('NDA Modal - User data:', onboardingData?.user);
    console.log('NDA Modal - Calculated partnerName:', partnerName);
    
    // Get existing NDA document data - only if NDA is actually completed
    const ndaData = onboardingData?.stepData?.nda;
    const isNdaCompleted = ndaData?.completed === true;
    
    const existingNDADocument = isNdaCompleted && ndaData ? {
      filename: ndaData.documentPath || ndaData.filename || 'NDA Agreement',
      completedAt: ndaData.completedAt,
      s3Url: ndaData.s3Url,
      s3Key: ndaData.s3Key
    } :  null;

    return (
      <NDAModal 
        isOpen={showNDATerms}
        onClose={handleCloseNDATerms}
        partnerName={partnerName}
        onStepComplete={handleNDAStepComplete}
        existingDocument={existingNDADocument}
      />
    );
  };

  // Setup Meeting Modal
  const renderSetupMeetingModal = () => {
    // Get partner name from login response (partnerInfo) or fallback to onboarding data
    const partnerName = getPartnerName();
    
    const partnerEmail = onboardingData?.user?.email || 
                        onboardingData?.user?.businessEmail ||
                        '';

    return (
      <SetupMeetingModal
        isOpen={showSetupMeeting}
        onClose={() => setShowSetupMeeting(false)}
        partnerName={partnerName}
        partnerEmail={partnerEmail}
        onStepComplete={handleMeetingStepComplete}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-[#1a4a1a]">
            {apiError ? 'Loading from backup data...' : 'Loading your partnership dashboard...'}
          </p>
          {apiError && (
            <p className="text-sm text-red-600 mt-2">
              API Error: {apiError}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] py-8 px-4 sm:px-6 lg:px-8">
      {/* NDA Terms Modal */}
      {renderNDATermsModal()}
      
      {/* Setup Meeting Modal */}
      {renderSetupMeetingModal()}
      
      {/* Partnership Terms Modal */}
      {renderPartnershipTermsModal()}
      
      {/* Completed Document Modal */}
      {/*renderCompletedDocumentModal()*/}
      
      {/* Partnership Document Modal */}
      {renderPartnershipDocumentModal()}
      
      {/* Shariyah Certification Modal */}
      {renderShariyahCertificationModal()}
      
      {/* Legal Compliance Modal */}
      {renderLegalComplianceModal()}
      
      {/* Tech Setup Modal */}
      {renderTechSetupModal()}
      
      {/* Training & Resources Modal */}
      {renderTrainingResourcesModal()}
      
      {/* Business Documentation Modal */}
      {renderBusinessDocumentationModal()}
      
      {/* Launch & Community Engagement Modal */}
      {renderLaunchEngagementModal()}
      
      <div className="max-w-7xl mx-auto">
        {/* API Error Display */}
        {apiError && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="text-red-600">⚠️</div>
                  <div>
                    <h3 className="font-semibold text-red-800">API Connection Issue</h3>
                    <p className="text-sm text-red-700">
                      Unable to connect to the server. Using local data as backup. 
                      Your progress will be saved locally until the connection is restored.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Onboarding Tips Section */}
        {!isLoading && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to Your Partnership Onboarding!</h3>
                    <p className="text-gray-700 mb-3">
                      Follow these steps to complete your partnership setup. Each step builds upon the previous one, 
                      so please complete them in order for the best experience.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">Complete each step to unlock the next</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Documents are automatically saved to secure storage</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-gray-600">Progress is saved automatically</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-gray-600">Need help? Contact us anytime</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#1a4a1a] mb-2">
                Partner Dashboard
              </h1>
              <p className="text-[#1a4a1a]/80 text-lg">
                Welcome back, {partnerData?.user?.firstName ? `${partnerData.user.firstName} ${partnerData.user.lastName}` : 'Partner'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {getDebugState().showDebugButton && (
                <Button
                  onClick={handleClearDashboardData}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50 text-sm"
                  title="Clear Dashboard Data (Debug Mode)"
                >
                  🗑️ Clear Data
                </Button>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Welcome Message */}
          <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200">
            <CardContent className="p-6 text-center">
              <div className="text-2xl mb-3">🤝</div>
              <h3 className="text-lg text-[#1a4a1a] font-medium mb-2">
                Welcome to Your Partnership Journey
              </h3>
              <p className="text-sm text-[#1a4a1a]/70">
                Track your progress through our partnership process with trust and transparency
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Partnership Status */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1a4a1a]">Partnership Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#1a4a1a] mb-2">
                    {(() => {
                      const steps = getLifecycleSteps();
                      return `${steps.filter(step => step.status === 'completed').length}/${steps.length}`;
                    })()}
                  </div>
                  <p className="text-[#1a4a1a]/70">Steps Completed</p>
                </div>
                <div className="text-center">
                  <Badge 
                    variant={(() => {
                      const steps = getLifecycleSteps();
                      return steps.filter(step => step.status === 'completed').length === steps.length ? 'default' : 'secondary';
                    })()}
                    className="text-lg px-4 py-2"
                  >
                    {(() => {
                      const steps = getLifecycleSteps();
                      return steps.filter(step => step.status === 'completed').length === steps.length ? 'COMPLETED' : 'IN PROGRESS';
                    })()}
                  </Badge>
                  <p className="text-[#1a4a1a]/70 mt-2">Current Status</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#1a4a1a] mb-2">
                    {partnerData?.estimatedCompletion || 'TBD'}
                  </div>
                  <p className="text-[#1a4a1a]/70">Estimated Completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Partnership Lifecycle */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1a4a1a]">Partnership Lifecycle</CardTitle>
              <p className="text-[#1a4a1a]/70">Track your progress through the partnership process</p>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Steps with flow arrows */}
                <div className="flex flex-wrap items-start justify-center gap-y-6 gap-x-2 md:gap-x-1">
                  {getLifecycleSteps().map((step, index) => {
                    const isEnabled = ConfigHelpers.isStepEnabled(step.key || step.id);
                    const isRequired = ConfigHelpers.isStepRequired(step.key || step.id);
                    const isInProgress = step.status === 'in_progress';
                    
                    return (
                      <React.Fragment key={step.id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: isEnabled ? 1 : 0.5, scale: 1 }}
                          transition={{ duration: 0.5, delay: 0.1 * index }}
                          className={`relative flex flex-col items-center min-w-[140px] max-w-[180px] ${!isEnabled ? 'opacity-50' : ''}`}
                        >
                          {/* Step Circle */}
                          <div className="flex flex-col items-center">
                            <button
                              onClick={() => handleStepClick(step.key || step.id)}
                              disabled={!isEnabled}
                              className={`
                                w-16 h-16 rounded-full flex items-center justify-center mb-3
                                border-2 shadow-lg transition-all duration-300
                                ${isEnabled 
                                  ? `${step.color} ${step.borderColor} hover:scale-110 cursor-pointer ${expandedStep === step.id ? 'ring-4 ring-emerald-300' : ''}`
                                  : 'bg-gray-300 border-gray-400 cursor-not-allowed'
                                }
                              `}
                              aria-label={`${isEnabled ? 'Click to view details for' : 'Disabled step:'} ${step.title}`}
                            >
                              {isEnabled ? renderStepIcon(step) : <X className="w-6 h-6 text-gray-500" />}
                            </button>
                          </div>
                          
                          {/* Step Info */}
                          <div className="text-center w-full">
                            <h3 className={`font-semibold text-sm mb-1 ${isEnabled ? `${step.textColor} cursor-pointer hover:underline` : 'text-gray-500 cursor-not-allowed'}`}
                                 onClick={() => isEnabled && handleStepClick(step.key || step.id)}>
                              {step.title}
                              {!isEnabled && <span className="text-xs text-gray-400 ml-1">(Disabled)</span>}
                            </h3>
                            <p className="text-xs text-[#1a4a1a]/60 mb-2">
                              {step.description}
                            </p>
                            <div className="flex flex-col gap-1.5 items-center">
                              {isInProgress && (
                                <Button
                                  size="sm"
                                  onClick={() => handleStepClick(step.key || step.id)}
                                  className="w-full max-w-[200px] bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 whitespace-normal leading-tight px-3"
                                >
                                  {getStepActionLabel(step.key)}
                                </Button>
                              )}
                              <Badge 
                                variant={step.status === 'completed' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {getStepStatusText(step.status)}
                              </Badge>
                              {!isRequired && (
                                <Badge variant="outline" className="text-xs text-gray-500">
                                  Optional
                                </Badge>
                              )}
                            </div>
                          </div>
                        </motion.div>
                        {/* Arrow between steps */}
                        {index < getLifecycleSteps().length - 1 && (
                          <div className="hidden md:flex items-center self-center mt-6 px-2 flex-shrink-0" aria-hidden="true">
                            <ArrowRight className="w-8 h-8 text-emerald-500 stroke-[2.5]" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Expandable Details */}
                <div className="mt-8">
                  {getLifecycleSteps().map((step) => (
                    <div key={`details-${step.id}`}>
                      {renderStepDetails(step)}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Support */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <p className="text-[#1a4a1a]/70 mb-4">
            Need assistance with any step? Our partnership team is here to help.
          </p>
          <Button
            onClick={() => window.location.href = 'mailto:salaam@halalcred.com?subject=Partnership%20Support'}
            className="bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white"
          >
            Contact Partnership Team
          </Button>
        </motion.div>

      </div>
    </div>
  );
};

export default PartnerDashboard; 